import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import Cookies from 'js-cookie';
import { adminAPI, authAPI, platformAPI, supportAPI } from '../api';
import { getTranslation, LANGUAGE_OPTIONS } from '../i18n';
import AdminAnalytics from './AdminAnalytics';
import AdminGuide from './AdminGuide';
import AdminDemoExperience from './AdminDemoExperience';
import LaunchAtelier from './LaunchAtelier';
import AdminOnboardingFlow from './AdminOnboardingFlow';
import AdminStorageUsage from './AdminStorageUsage';
import AdminDatabaseBackups from './AdminDatabaseBackups';
import AdminLegalPages from './AdminLegalPages';
import AdminEmailLog from './AdminEmailLog';
import AdminTrashBoards from './AdminTrashBoards';
import AdminMessages from './AdminMessages';
import AdminActivityTab from './AdminActivityTab';
import SystemHealthCard from '../components/SystemHealthCard';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const AdminPanel = () => {
  const navigate = useNavigate();
  document.title = 'Maestra - Admin';

  const [tab, setTab] = useState('overview');
  const [users, setUsers] = useState([]);
  const [stats, setStats] = useState(null);
  const [templates, setTemplates] = useState([]);
  // Plans + feature key registry — populated by /admin/plans. Empty until
  // the first fetch resolves; PlansTab / UserDetailsDrawer both render a
  // calm placeholder while loading.
  const [plans, setPlans] = useState([]);
  const [featureKeys, setFeatureKeys] = useState([]);
  // Side drawer for per-user details (template + plan + overrides). One
  // user-id at a time, null when closed.
  const [userDrawer, setUserDrawer] = useState(null);
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState(null);
  const [workspaceLang, setWorkspaceLang] = useState('en');
  const [unreadMessages, setUnreadMessages] = useState(0);

  // Invite modal
  const [showInvite, setShowInvite] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteUsername, setInviteUsername] = useState('');
  const [inviteStorage, setInviteStorage] = useState(500);
  const [inviteMaxBoards, setInviteMaxBoards] = useState('');
  const [inviteTemplateId, setInviteTemplateId] = useState('');
  const [inviteResult, setInviteResult] = useState(null);

  // Limits modal
  const [showLimits, setShowLimits] = useState(null);
  const [limitStorage, setLimitStorage] = useState(500);
  const [limitMaxBoards, setLimitMaxBoards] = useState('');

  // Template modal
  const [showTemplateModal, setShowTemplateModal] = useState(false);
  const [templateName, setTemplateName] = useState('');
  const [templateLabels, setTemplateLabels] = useState('Mood, Light, Pose, Wardrobe, Atmosphere');
  const [templateLang, setTemplateLang] = useState('en');

  // Delete modal
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [permanentDeleteTarget, setPermanentDeleteTarget] = useState(null);
  const [deletedUsers, setDeletedUsers] = useState([]);

  // Access requests
  const [accessRequests, setAccessRequests] = useState([]);
  const [requestFilter, setRequestFilter] = useState('all');

  // User management search & filters
  const [userSearch, setUserSearch] = useState('');
  const [userFilter, setUserFilter] = useState('all');
  const [openDropdown, setOpenDropdown] = useState(null);

  // Manage access modal
  // Manage modal (Access controls + custom branding)
  const [accessTarget, setAccessTarget] = useState(null);

  // Renewals
  const [renewalRequests, setRenewalRequests] = useState([]);
  const [renewalHistory, setRenewalHistory] = useState([]);
  const [renewalSubTab, setRenewalSubTab] = useState('requests');
  const [paymentModal, setPaymentModal] = useState(null);

  // Platform texts
  const [ptx, setPtx] = useState({
    login_title: '', login_description: '',
    footer_text: '', footer_subtext: '', footer_link: '', footer_link_label: '',
    empty_state_boards: '',
    home_title: '', home_subtitle: '', home_note: '', home_cta: '', home_secondary_cta: '',
    invite_title: '', invite_description: '',
    request_access_title: '', request_access_description: '', request_access_cta_label: '', request_access_success_message: '',
    request_access_audience_tags: [],
    request_access_benefits: [],
    pdf_footer_basic_title: '', pdf_footer_basic_subtext: '', pdf_footer_basic_attribution: '',
    pdf_footer_pro_attribution: '',
  });
  const [savingPtx, setSavingPtx] = useState(false);
  const [ptxDirty, setPtxDirty] = useState(false);

  const t = getTranslation(workspaceLang);

  const updatePtxField = (key, value) => {
    setPtx(prev => ({ ...prev, [key]: value }));
    setPtxDirty(true);
  };

  const handleSavePtx = async () => {
    setSavingPtx(true);
    try {
      const { data } = await platformAPI.updateTexts(ptx);
      setPtx(data);
      setPtxDirty(false);
      showToast('Platform texts saved');
    } catch (err) {
      showToast(err.response?.data?.detail || 'Failed to save');
    } finally {
      setSavingPtx(false);
    }
  };

  const fetchAll = useCallback(async ({ silent = false } = {}) => {
    if (!silent) setLoading(true);
    try {
      const [usersRes, statsRes, templatesRes, plansRes, settingsRes, deletedRes, ptxRes, requestsRes, renewReqRes, renewHistRes, messagesRes] = await Promise.all([
        adminAPI.getUsers(),
        adminAPI.getStats(),
        adminAPI.getTemplates(),
        adminAPI.getPlans().catch(() => ({ data: { plans: [], feature_keys: [] } })),
        authAPI.getSettings(),
        adminAPI.getDeletedUsers(),
        platformAPI.getTexts(),
        platformAPI.getAccessRequests(),
        adminAPI.getRenewalRequests(),
        adminAPI.getRenewalHistory(),
        supportAPI.listAdmin('new').catch(() => ({ data: { counts: { new: 0 } } })),
      ]);
      setUsers(usersRes.data);
      setStats(statsRes.data);
      setTemplates(templatesRes.data);
      setPlans(plansRes.data?.plans || []);
      setFeatureKeys(plansRes.data?.feature_keys || []);
      setWorkspaceLang(settingsRes.data.language || 'en');
      setDeletedUsers(deletedRes.data);
      setPtx(ptxRes.data);
      setAccessRequests(requestsRes.data);
      setRenewalRequests(renewReqRes.data);
      setRenewalHistory(renewHistRes.data);
      setUnreadMessages(messagesRes?.data?.counts?.new || 0);
    } catch (err) {
      if (err.response?.status === 403) {
        navigate('/dashboard');
      }
    } finally {
      if (!silent) setLoading(false);
    }
  }, [navigate]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const showToast = (msg) => {
    setToast(msg);
    setTimeout(() => setToast(null), 3000);
  };

  const handleInvite = async () => {
    if (!inviteEmail) return;
    try {
      const { data } = await adminAPI.inviteUser({
        email: inviteEmail,
        username: inviteUsername || undefined,
        storage_limit_mb: inviteStorage,
        max_boards: inviteMaxBoards ? parseInt(inviteMaxBoards) : null,
        template_id: inviteTemplateId || undefined,
      });
      setInviteResult(data);
      fetchAll();
    } catch (err) {
      showToast(err.response?.data?.detail || 'Invite failed');
    }
  };

  const handleStatusChange = async (userId, newStatus) => {
    try {
      await adminAPI.updateUserStatus(userId, newStatus);
      fetchAll();
      showToast(`User ${newStatus}`);
    } catch (err) {
      showToast(err.response?.data?.detail || 'Failed');
    }
  };

  const handleLimitsSubmit = async () => {
    if (!showLimits) return;
    try {
      await adminAPI.updateUserLimits(showLimits, {
        storage_limit_mb: limitStorage,
        max_boards: limitMaxBoards ? parseInt(limitMaxBoards) : null,
      });
      setShowLimits(null);
      fetchAll();
      showToast('Limits updated');
    } catch (err) {
      showToast('Failed to update limits');
    }
  };

  const handleSoftDelete = async () => {
    if (!deleteTarget) return;
    try {
      await adminAPI.deleteUser(deleteTarget.id);
      setDeleteTarget(null);
      fetchAll();
      showToast('User deleted. Data retained for 30 days.');
    } catch (err) {
      showToast(err.response?.data?.detail || 'Failed to delete user');
    }
  };

  const handleRestore = async (userId) => {
    try {
      await adminAPI.restoreUser(userId);
      fetchAll();
      showToast('User restored');
    } catch (err) {
      showToast(err.response?.data?.detail || 'Failed to restore user');
    }
  };

  const handlePermanentDelete = async () => {
    if (!permanentDeleteTarget) return;
    try {
      await adminAPI.permanentDeleteUser(permanentDeleteTarget.id);
      setPermanentDeleteTarget(null);
      fetchAll();
      showToast('User permanently deleted');
    } catch (err) {
      showToast(err.response?.data?.detail || 'Failed to delete user');
    }
  };

  const handleCreateTemplate = async () => {
    if (!templateName) return;
    try {
      await adminAPI.createTemplate({
        name: templateName,
        labels: templateLabels.split(',').map(l => l.trim()).filter(Boolean),
        client_language: templateLang,
      });
      setShowTemplateModal(false);
      setTemplateName('');
      fetchAll();
      showToast('Template created');
    } catch (err) {
      showToast(err.response?.data?.detail || 'Failed to create template');
    }
  };

  const handleDeleteTemplate = async (id) => {
    try {
      await adminAPI.deleteTemplate(id);
      fetchAll();
      showToast('Template deleted');
    } catch (err) {
      showToast(err.response?.data?.detail || 'Cannot delete');
    }
  };

  const copyInviteLink = (inviteUrl) => {
    const fullUrl = `${window.location.origin}${inviteUrl}`;
    navigator.clipboard.writeText(fullUrl);
    showToast(t.invite_link_copied || 'Invite link copied!');
  };

  const handleLogout = () => {
    Cookies.remove('kahla_token');
    Cookies.remove('kahla_role');
    navigate('/login');
  };

  if (loading) {
    return (
      <div className="dashboard fade-in" style={{ display: 'flex', justifyContent: 'center', padding: '80px' }}>
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <div className="dashboard fade-in">
      {/* Header */}
      <header className="dashboard-header">
        <div>
          <p style={{
            fontFamily: 'Playfair Display, serif',
            fontSize: '12px',
            letterSpacing: '0.3em',
            textTransform: 'uppercase',
            color: 'var(--kahla-text-light)',
            marginBottom: '8px'
          }}>{t.studio_name}</p>
          <h1 className="dashboard-title">{t.admin_panel || 'Admin Panel'}</h1>
        </div>
        <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
          <button className="btn-secondary" data-testid="back-to-dashboard" onClick={() => navigate('/dashboard')}>
            {t.back_to_dashboard || 'Back to Dashboard'}
          </button>
          <button className="btn-secondary" onClick={handleLogout}>
            {t.sign_out}
          </button>
        </div>
      </header>

      {/* ============================================================
           Navigation — two-level, calm, editorial.
           ============================================================
           A single 12-item tab strip felt like enterprise SaaS. Grouping
           into 4 calm sections (Overview / Workspaces / Experience /
           Platform) lets the admin scan with intent, not search.
      */}
      {(() => {
        const pendingRequests = accessRequests.filter(r => r.status === 'pending').length;
        const pendingRenewals = renewalRequests.length;

        const SECTIONS = [
          { key: 'overview',   label: 'Overview',   tabs: [] },
          { key: 'users',      label: 'Users',      tabs: ['users', 'requests', 'renewals', 'messages'] },
          { key: 'workspaces', label: 'Workspaces', tabs: ['templates', 'plans', 'onboarding', 'demo', 'guide'] },
          { key: 'atelier',    label: 'Atelier',    tabs: ['atelier'] },
          { key: 'platform',   label: 'Platform',   tabs: ['storage', 'backups', 'activity', 'trash', 'analytics', 'platform', 'email_log', 'legal'] },
        ];
        const TAB_LABEL = {
          users: t.user_management || 'Users',
          requests: 'Requests',
          renewals: 'Renewals',
          messages: 'Messages',
          templates: t.workspace_templates || 'Templates',
          plans: 'Plans',
          onboarding: 'Onboarding',
          demo: 'Demo',
          guide: 'Guide',
          atelier: 'Launch Atelier',
          storage: 'Storage',
          backups: 'Backups',
          activity: 'Activity',
          trash: 'Trash',
          analytics: 'Analytics',
          platform: 'Settings',
          email_log: 'Email log',
          legal: 'Legal',
        };
        const tabToSection = {};
        SECTIONS.forEach(s => s.tabs.forEach(tt => { tabToSection[tt] = s.key; }));
        const activeSection = tab === 'overview' ? 'overview' : (tabToSection[tab] || 'users');
        const sectionDef = SECTIONS.find(s => s.key === activeSection);

        const sectionHasDot = (key) => {
          if (key === 'users') return pendingRequests > 0 || pendingRenewals > 0 || unreadMessages > 0;
          return false;
        };
        const tabDot = (tt) => {
          if (tt === 'requests' && pendingRequests > 0) return '#c17c3e';
          if (tt === 'renewals' && pendingRenewals > 0) return '#1565c0';
          if (tt === 'messages' && unreadMessages > 0) return '#cf9a3d';
          return null;
        };

        const handleSection = (key) => {
          if (key === 'overview') { setTab('overview'); return; }
          const first = SECTIONS.find(s => s.key === key)?.tabs?.[0];
          if (first) setTab(first);
        };

        return (
          <>
            {/* Primary section nav — Playfair, calm, generous spacing */}
            <nav
              data-testid="admin-section-nav"
              style={{
                display: 'flex', gap: 44, alignItems: 'baseline',
                marginBottom: 20,
                paddingBottom: 18,
                borderBottom: '1px solid var(--kahla-border)',
                flexWrap: 'wrap',
              }}
            >
              {SECTIONS.map(s => {
                const isActive = activeSection === s.key;
                return (
                  <button
                    key={s.key}
                    type="button"
                    data-testid={`section-${s.key}`}
                    onClick={() => handleSection(s.key)}
                    style={{
                      background: 'none', border: 'none', padding: '4px 0',
                      cursor: 'pointer',
                      fontFamily: 'Playfair Display, serif',
                      fontSize: 22, fontWeight: 400,
                      letterSpacing: '-0.005em',
                      color: isActive ? 'var(--kahla-charcoal, #2a2a2a)' : 'var(--kahla-text-light)',
                      opacity: isActive ? 1 : 0.78,
                      position: 'relative',
                      transition: 'color 0.18s ease, opacity 0.18s ease',
                    }}
                  >
                    {s.label}
                    {sectionHasDot(s.key) && (
                      <span
                        aria-hidden="true"
                        style={{
                          position: 'absolute', top: 4, right: -14,
                          width: 7, height: 7, borderRadius: '50%',
                          background: '#cf9a3d',
                        }}
                      />
                    )}
                  </button>
                );
              })}
            </nav>

            {/* Secondary sub-tabs — small, quiet, only visible when a section
                has more than one entry. */}
            {sectionDef && sectionDef.tabs.length > 0 && (
              <div
                data-testid="admin-subtabs"
                style={{
                  display: 'flex', gap: 4, alignItems: 'center',
                  marginBottom: 28,
                  flexWrap: 'wrap',
                }}
              >
                {sectionDef.tabs.map((tt) => {
                  const isActive = tab === tt;
                  const dot = tabDot(tt);
                  return (
                    <button
                      key={tt}
                      type="button"
                      data-testid={`tab-${tt}`}
                      onClick={() => setTab(tt)}
                      style={{
                        background: 'none', border: 'none',
                        padding: '6px 14px',
                        fontFamily: 'inherit',
                        fontSize: 11,
                        letterSpacing: '0.18em',
                        textTransform: 'uppercase',
                        color: isActive ? 'var(--kahla-charcoal, #2a2a2a)' : 'var(--kahla-text-light)',
                        fontWeight: isActive ? 600 : 400,
                        cursor: 'pointer',
                        position: 'relative',
                        transition: 'color 0.18s ease',
                      }}
                    >
                      {TAB_LABEL[tt] || tt}
                      {dot && (
                        <span
                          data-testid={tt === 'messages' ? 'messages-unread-dot' : undefined}
                          aria-hidden="true"
                          style={{
                            position: 'absolute', top: 4, right: 2,
                            width: 6, height: 6, borderRadius: '50%',
                            background: dot,
                          }}
                        />
                      )}
                    </button>
                  );
                })}
              </div>
            )}
          </>
        );
      })()}

      {/* ============================================================
           Overview — calm, editorial homepage.
           ============================================================
           Replaced the boxed 4-card grid. Numbers breathe with Playfair
           typography and generous whitespace; the supporting label sits
           below in soft taupe tracking. No borders, no equal cards.
      */}
      {tab === 'overview' && stats && (
        <div
          data-testid="admin-overview"
          style={{ paddingTop: 8, paddingBottom: 36, maxWidth: 1100 }}
        >
          <p style={{
            fontFamily: 'Cormorant Garamond, Playfair Display, serif',
            fontStyle: 'italic', fontSize: 17, lineHeight: 1.65,
            color: 'var(--kahla-text-light)', margin: '0 0 36px',
            maxWidth: 620,
          }}>
            A calm snapshot of the studio. Use the sections above to manage
            workspaces, refine the client experience, and tend to the
            platform itself.
          </p>

          <div
            data-testid="admin-stats"
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
              columnGap: 56, rowGap: 36,
            }}
          >
            {[
              { label: t.total_users || 'Users', value: stats.users?.total || 0, sub: `${stats.users?.active || 0} ${t.active || 'active'}` },
              { label: t.total_boards || 'Boards', value: stats.boards || 0, sub: '' },
              { label: t.total_images || 'Images', value: stats.images || 0, sub: '' },
              { label: 'Open requests', value: accessRequests.filter(r => r.status === 'pending').length, sub: accessRequests.filter(r => r.status === 'pending').length > 0 ? 'awaiting response' : 'all clear' },
            ].map((s, i) => (
              <div key={i} data-testid={`overview-stat-${i}`}>
                <div style={{
                  fontFamily: 'Playfair Display, serif',
                  fontSize: 44, fontWeight: 400,
                  color: 'var(--kahla-charcoal, #2a2a2a)',
                  lineHeight: 1.05,
                  letterSpacing: '-0.01em',
                }}>{s.value}</div>
                <div style={{
                  fontSize: 10,
                  letterSpacing: '0.22em',
                  textTransform: 'uppercase',
                  color: 'var(--kahla-text-light)',
                  marginTop: 10,
                }}>{s.label}</div>
                {s.sub && (
                  <div style={{
                    fontFamily: 'Cormorant Garamond, Playfair Display, serif',
                    fontStyle: 'italic',
                    fontSize: 13, lineHeight: 1.55,
                    color: 'var(--kahla-text-light)',
                    marginTop: 6,
                    opacity: 0.85,
                  }}>{s.sub}</div>
                )}
              </div>
            ))}
          </div>

          <SystemHealthCard />
        </div>
      )}

      {/* Users Tab */}
      {tab === 'users' && (
        <div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px', gap: '12px', flexWrap: 'wrap' }}>
            {/* Search */}
            <input
              data-testid="user-search"
              type="text"
              placeholder="Search by name or email..."
              value={userSearch}
              onChange={e => setUserSearch(e.target.value)}
              style={{
                padding: '8px 14px', fontSize: '13px', border: '1px solid var(--kahla-border)',
                background: 'var(--kahla-white)', fontFamily: 'inherit', width: '260px',
                boxSizing: 'border-box',
              }}
            />
            {/* Filters */}
            <div style={{ display: 'flex', gap: '6px', flex: 1, justifyContent: 'center' }}>
              {['all', 'active', 'expiring', 'expired'].map(f => (
                <button
                  key={f}
                  data-testid={`user-filter-${f}`}
                  onClick={() => setUserFilter(f)}
                  style={{
                    padding: '6px 14px', fontSize: '11px', letterSpacing: '0.04em',
                    textTransform: 'capitalize', fontFamily: 'inherit', cursor: 'pointer',
                    border: '1px solid var(--kahla-border)',
                    background: userFilter === f ? 'var(--kahla-text)' : 'transparent',
                    color: userFilter === f ? 'var(--kahla-white)' : 'var(--kahla-text-light)',
                  }}
                >{f === 'expiring' ? 'Expiring soon' : f}</button>
              ))}
            </div>
            <button className="btn-primary" data-testid="invite-user-btn" onClick={() => { setShowInvite(true); setInviteResult(null); setInviteEmail(''); setInviteUsername(''); }}>
              {t.invite_user || 'Invite User'}
            </button>
          </div>

          {/* Users table */}
          <div style={{ overflowX: 'auto' }}>
            <table data-testid="users-table" style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
              <thead>
                <tr style={{ borderBottom: '2px solid var(--kahla-border)', textAlign: 'left' }}>
                  <th style={thStyle}>User</th>
                  <th style={thStyle}>Access</th>
                  <th style={thStyle}>Access period</th>
                  <th style={thStyle}>{t.boards || 'Boards'}</th>
                  <th style={thStyle}>{t.storage || 'Storage'}</th>
                  <th style={thStyle}>Branding</th>
                  <th style={thStyle}>Support</th>
                  <th style={thStyle}>{t.actions || 'Actions'}</th>
                </tr>
              </thead>
              <tbody>
                {users
                  .filter(u => {
                    const q = userSearch.toLowerCase();
                    if (q && !u.username.toLowerCase().includes(q) && !(u.email || '').toLowerCase().includes(q)) return false;
                    if (userFilter === 'active') return u.access_status === 'active' || u.access_status === 'admin' || u.access_status === 'trial';
                    if (userFilter === 'expiring') return u.days_left != null && u.days_left <= 7 && (u.access_status === 'active' || u.access_status === 'trial');
                    if (userFilter === 'expired') return u.access_status === 'expired_read_only' || u.access_status === 'archived_frozen' || u.access_status === 'deactivated';
                    return true;
                  })
                  .map(u => (
                  <tr key={u.id} data-testid={`user-row-${u.username}`} style={{ borderBottom: '1px solid var(--kahla-border)' }}>
                    <td style={tdStyle}>
                      <div>
                        <div style={{ fontSize: '13px', fontWeight: '500', color: 'var(--kahla-text)' }}>{u.username}</div>
                        {u.email ? (
                          <a
                            href={`mailto:${u.email}`}
                            data-testid={`user-email-${u.username}`}
                            title={`Email ${u.username}`}
                            style={{
                              display: 'inline-block',
                              fontSize: '12px',
                              color: 'var(--kahla-text-light)',
                              marginTop: '2px',
                              textDecoration: 'underline',
                              textUnderlineOffset: '3px',
                              textDecorationColor: 'rgba(166,152,136,0.45)',
                              wordBreak: 'break-all',
                            }}
                            onClick={(e) => e.stopPropagation()}
                          >
                            {u.email}
                          </a>
                        ) : (
                          <span
                            data-testid={`user-email-empty-${u.username}`}
                            style={{
                              fontFamily: 'Cormorant Garamond, Playfair Display, serif',
                              fontStyle: 'italic',
                              fontSize: '12px',
                              color: 'var(--kahla-text-light)',
                              opacity: 0.7,
                              marginTop: '2px',
                              display: 'inline-block',
                            }}
                          >no email on file</span>
                        )}
                        <div style={{ display: 'flex', gap: '4px', marginTop: '4px', flexWrap: 'wrap' }}>
                          {u.role === 'admin' && <span style={{ fontSize: '9px', padding: '1px 6px', background: '#f0e6d3', textTransform: 'uppercase', letterSpacing: '0.05em', color: '#5d4037' }}>admin</span>}
                          {u.template_id && (() => { const tpl = templates.find(t => t.id === u.template_id); return tpl ? <span style={{ fontSize: '9px', padding: '1px 6px', background: '#f5f5f5', color: 'var(--kahla-text-light)', letterSpacing: '0.03em' }}>{tpl.name}</span> : null; })()}
                          {u.plan_id && (() => { const p = plans.find(pp => pp.id === u.plan_id); return p ? <span style={{ fontSize: '9px', padding: '1px 6px', background: '#efe7d6', color: '#7a5b2e', letterSpacing: '0.05em', textTransform: 'uppercase' }}>{p.name}</span> : null; })()}
                          {u.feature_overrides && Object.keys(u.feature_overrides).length > 0 && (
                            <span title={`${Object.keys(u.feature_overrides).length} feature override(s)`} style={{ fontSize: '9px', padding: '1px 6px', background: 'transparent', border: '1px solid #d8cab0', color: '#7a5b2e', letterSpacing: '0.05em' }}>
                              {Object.keys(u.feature_overrides).length} override{Object.keys(u.feature_overrides).length === 1 ? '' : 's'}
                            </span>
                          )}
                        </div>
                      </div>
                    </td>
                    <td style={tdStyle}>
                      <AccessStatusBadge status={u.access_status} />
                    </td>
                    <td style={tdStyle}>
                      <UserAccessPeriod user={u} />
                    </td>
                    <td style={tdStyle}>
                      <span style={{ fontSize: '12px' }}>{u.board_count}{u.max_boards != null ? ` / ${u.max_boards}` : ''}</span>
                    </td>
                    <td style={tdStyle}>
                      <span style={{ fontSize: '11px', color: 'var(--kahla-text-light)' }}>
                        {formatStorage(u.storage_used_mb)}{u.storage_limit_mb > 0 ? ` / ${formatStorage(u.storage_limit_mb)}` : ''}
                      </span>
                    </td>
                    <td style={tdStyle}>
                      {u.role === 'admin' ? (
                        <span style={{ fontSize: '11px', color: 'var(--kahla-text-light)', fontStyle: 'italic' }}>—</span>
                      ) : (
                        <BrandingPill user={u} onAction={fetchAll} showToast={showToast} />
                      )}
                    </td>
                    <td style={tdStyle}>
                      <SupportAccessCell user={u} showToast={showToast} />
                    </td>
                    <td style={tdStyle}>
                      {u.role === 'admin' ? (
                        <span style={{ fontSize: '11px', color: 'var(--kahla-text-light)', fontStyle: 'italic' }}>—</span>
                      ) : (
                        <UserActionsCell user={u} onManage={() => setAccessTarget(u)} onOpenDrawer={() => setUserDrawer(u.id)} onAction={fetchAll} showToast={showToast} t={t} copyInviteLink={copyInviteLink} setDeleteTarget={setDeleteTarget} handleStatusChange={handleStatusChange} openDropdown={openDropdown} setOpenDropdown={setOpenDropdown} />
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Templates Tab */}
      {tab === 'templates' && (
        <div>
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '16px' }}>
            <button className="btn-primary" data-testid="create-template-btn" onClick={() => setShowTemplateModal(true)}>
              {t.create_template || 'Create Template'}
            </button>
          </div>

          <div style={{ display: 'grid', gap: '16px' }}>
            {templates.map(tmpl => (
              <div key={tmpl.id} data-testid={`template-${tmpl.name}`} style={{
                background: 'var(--kahla-white)',
                border: '1px solid var(--kahla-border)',
                padding: '20px',
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                  <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                    <h3 style={{ fontSize: '15px', fontWeight: '600', margin: 0 }}>{tmpl.name}</h3>
                    {tmpl.is_global && (
                      <span style={{ fontSize: '10px', padding: '2px 6px', background: '#f0e6d3', borderRadius: '2px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                        {t.global_template || 'Global'}
                      </span>
                    )}
                  </div>
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <button
                      data-testid={`edit-template-${tmpl.name}`}
                      onClick={() => navigate(`/admin/templates/${tmpl.id}`)}
                      style={{ ...actionBtnStyle('#5d4037'), fontSize: '11px' }}
                    >
                      Edit
                    </button>
                    {!tmpl.is_global && (
                      <button
                        onClick={() => handleDeleteTemplate(tmpl.id)}
                        style={{ ...actionBtnStyle('#c62828'), fontSize: '11px' }}
                      >
                        {t.delete || 'Delete'}
                      </button>
                    )}
                  </div>
                </div>
                <div style={{ display: 'flex', gap: '24px', flexWrap: 'wrap', fontSize: '12px', color: 'var(--kahla-text-light)' }}>
                  <div><strong>{t.default_labels || 'Labels'}:</strong> {(tmpl.labels || []).join(', ')}</div>
                  <div><strong>{t.client_language || 'Client Language'}:</strong> {tmpl.client_language}</div>
                  {tmpl.reaction_mode && tmpl.reaction_mode !== 'mood' && (
                    <div>Reactions: <span style={{ textTransform: 'capitalize' }}>{tmpl.reaction_mode}</span></div>
                  )}
                  {tmpl.texts && Object.values(tmpl.texts).some(v => v) && (
                    <div style={{ width: '100%', marginTop: '4px', fontSize: '11px', fontStyle: 'italic' }}>
                      Custom texts configured
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Plans Tab — editorial table view (rows = features, columns = plans).
          Plans are feature packages assigned to users; templates handle
          workflow defaults separately. */}
      {tab === 'plans' && (
        <PlansTab
          plans={plans}
          featureKeys={featureKeys}
          onChange={() => fetchAll({ silent: true })}
          showToast={showToast}
        />
      )}


      {/* Access Requests Tab */}
      {tab === 'requests' && (
        <div data-testid="access-requests-panel">
          {/* Filter tabs */}
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginBottom: '20px' }}>
            {['all', 'pending', 'approved', 'declined', 'archived'].map(f => (
              <button
                key={f}
                data-testid={`filter-${f}`}
                onClick={() => setRequestFilter(f)}
                style={{
                  padding: '6px 14px', fontSize: '11px', letterSpacing: '0.04em',
                  textTransform: 'capitalize', border: '1px solid var(--kahla-border)',
                  background: requestFilter === f ? 'var(--kahla-text)' : 'transparent',
                  color: requestFilter === f ? 'var(--kahla-white)' : 'var(--kahla-text-light)',
                  cursor: 'pointer', fontFamily: 'inherit',
                }}
              >
                {f} ({f === 'all' ? accessRequests.filter(r => r.status !== 'archived').length : accessRequests.filter(r => r.status === f).length})
              </button>
            ))}
          </div>

          {/* Requests list */}
          {accessRequests.filter(r => requestFilter === 'all' ? r.status !== 'archived' : r.status === requestFilter).length === 0 ? (
            <div style={{ textAlign: 'center', padding: '48px 24px', color: 'var(--kahla-text-light)', fontSize: '13px' }}>
              No {requestFilter === 'all' ? '' : requestFilter} requests yet.
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              {accessRequests
                .filter(r => requestFilter === 'all' ? r.status !== 'archived' : r.status === requestFilter)
                .map((req, i) => (
                  <div key={req.email + i} data-testid={`request-row-${req.email}`} style={{ background: 'var(--kahla-white)', border: '1px solid var(--kahla-border)', padding: '16px 20px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap', marginBottom: '6px' }}>
                      <span style={{ fontSize: '14px', fontWeight: '600', color: 'var(--kahla-text)' }}>{req.first_name} {req.last_name}</span>
                      <span style={{ fontSize: '12px', color: 'var(--kahla-text-light)' }}>{req.email}</span>
                      <div style={{ marginLeft: 'auto' }}><RequestStatusBadge status={req.status} /></div>
                    </div>
                    <div style={{ fontSize: '12px', color: 'var(--kahla-text-light)', display: 'flex', flexWrap: 'wrap', gap: '16px', marginBottom: '6px' }}>
                      <span>{req.profession}</span>
                      {req.instagram_or_website && <span>{req.instagram_or_website}</span>}
                      {req.team_size && <span>Team: {req.team_size}</span>}
                      <span style={{ opacity: 0.7 }}>{new Date(req.created_at).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}</span>
                    </div>
                    <div style={{ display: 'flex', gap: '8px', alignItems: 'center', borderTop: '1px solid var(--kahla-border)', paddingTop: '10px', marginTop: '4px' }}>
                      {req.status === 'pending' && (
                        <>
                          <button data-testid={`approve-${req.email}`} onClick={async () => { try { await platformAPI.updateAccessRequestStatus(req.email, 'approved'); fetchAll(); showToast('Approved'); } catch { showToast('Failed'); } }} style={actionBtnStyle('#2e7d32')}>Approve</button>
                          <button data-testid={`decline-${req.email}`} onClick={async () => { try { await platformAPI.updateAccessRequestStatus(req.email, 'declined'); fetchAll(); showToast('Declined'); } catch { showToast('Failed'); } }} style={actionBtnStyle('#c62828')}>Decline</button>
                        </>
                      )}
                      {req.status === 'approved' && (
                        <button data-testid={`invite-from-request-${req.email}`} onClick={() => { setTab('users'); setShowInvite(true); setInviteResult(null); setInviteEmail(req.email); setInviteUsername(req.first_name); }} style={actionBtnStyle('#5d4037')}>Create Invite</button>
                      )}
                      {req.status === 'declined' && (
                        <button data-testid={`reopen-${req.email}`} onClick={async () => { try { await platformAPI.updateAccessRequestStatus(req.email, 'pending'); fetchAll(); showToast('Reopened'); } catch { showToast('Failed'); } }} style={actionBtnStyle('#5d4037')}>Reopen</button>
                      )}
                      {req.status === 'archived' && (
                        <button data-testid={`unarchive-${req.email}`} onClick={async () => { try { await platformAPI.updateAccessRequestStatus(req.email, 'pending'); fetchAll(); showToast('Restored'); } catch { showToast('Failed'); } }} style={actionBtnStyle('#5d4037')}>Restore</button>
                      )}
                      <div style={{ marginLeft: 'auto', display: 'flex', gap: '6px' }}>
                        {req.status !== 'archived' && (
                          <button data-testid={`archive-req-${req.email}`} onClick={async () => { try { await platformAPI.updateAccessRequestStatus(req.email, 'archived'); fetchAll(); showToast('Archived'); } catch { showToast('Failed'); } }} style={{ ...actionBtnStyle('#9e9e9e'), fontSize: '10px', padding: '3px 8px' }}>Archive</button>
                        )}
                        <button data-testid={`delete-req-${req.email}`} onClick={async () => { if (window.confirm('Permanently delete this request?')) { try { await platformAPI.deleteAccessRequest(req.email); fetchAll(); showToast('Deleted'); } catch { showToast('Failed'); } } }} style={{ ...actionBtnStyle('#c62828'), fontSize: '10px', padding: '3px 8px' }}>Delete</button>
                      </div>
                    </div>
                  </div>
                ))}
            </div>
          )}
        </div>
      )}

      {/* Renewals Tab */}
      {tab === 'renewals' && (
        <div data-testid="renewals-panel">
          {/* Sub-tabs */}
          <div style={{ display: 'flex', gap: '16px', marginBottom: '24px' }}>
            {['requests', 'history'].map(st => (
              <button
                key={st}
                data-testid={`renewal-subtab-${st}`}
                onClick={() => setRenewalSubTab(st)}
                style={{
                  padding: '8px 20px', fontSize: '12px', letterSpacing: '0.04em',
                  textTransform: 'capitalize', fontFamily: 'inherit', cursor: 'pointer',
                  border: '1px solid var(--kahla-border)',
                  background: renewalSubTab === st ? 'var(--kahla-text)' : 'transparent',
                  color: renewalSubTab === st ? 'var(--kahla-white)' : 'var(--kahla-text-light)',
                }}
              >{st === 'requests' ? `Requests (${renewalRequests.length})` : 'History'}</button>
            ))}
            <button
              data-testid="check-expiring-btn"
              onClick={async () => {
                try {
                  const { data } = await adminAPI.checkExpiringUsers();
                  showToast(`Expiry reminders sent: ${data.reminders_sent}`);
                } catch (err) { showToast('Failed'); }
              }}
              style={{
                marginLeft: 'auto', padding: '8px 16px', fontSize: '11px',
                border: '1px solid var(--kahla-border)', background: 'transparent',
                color: 'var(--kahla-text-light)', cursor: 'pointer', fontFamily: 'inherit',
              }}
            >Send expiry reminders</button>
          </div>

          {/* Requests sub-tab */}
          {renewalSubTab === 'requests' && (
            <div>
              {renewalRequests.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '48px 24px', color: 'var(--kahla-text-light)', fontSize: '13px' }}>
                  No pending renewal requests.
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  {renewalRequests.map(req => (
                    <RenewalRequestCard
                      key={req.id}
                      req={req}
                      onAction={fetchAll}
                      showToast={showToast}
                      onPaymentLink={() => setPaymentModal(req)}
                    />
                  ))}
                </div>
              )}
            </div>
          )}

          {/* History sub-tab */}
          {renewalSubTab === 'history' && (
            <div>
              {renewalHistory.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '48px 24px', color: 'var(--kahla-text-light)', fontSize: '13px' }}>
                  No renewal history yet.
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                  {renewalHistory.map(req => (
                    <RenewalHistoryCard key={req.id} req={req} onAction={fetchAll} showToast={showToast} />
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Payment Link Modal */}
      {paymentModal && (
        <PaymentLinkModal
          req={paymentModal}
          onClose={() => setPaymentModal(null)}
          onSent={() => { setPaymentModal(null); fetchAll(); }}
          showToast={showToast}
        />
      )}

      {/* Analytics Tab */}
      {tab === 'analytics' && (
        <AdminAnalytics />
      )}

      {/* Guide Tab */}
      {tab === 'guide' && (
        <AdminGuide />
      )}

      {/* Launch Atelier Tab — admin creative direction workspace */}
      {tab === 'atelier' && (
        <LaunchAtelier />
      )}

      {/* Demo Experience Tab */}
      {tab === 'demo' && (
        <AdminDemoExperience />
      )}

      {/* Onboarding Flow Tab */}
      {tab === 'onboarding' && (
        <AdminOnboardingFlow />
      )}

      {/* Storage Usage Tab */}
      {tab === 'storage' && (
        <AdminStorageUsage />
      )}

      {/* Backups Tab */}
      {tab === 'backups' && (
        <AdminDatabaseBackups />
      )}

      {/* Legal Pages Tab */}
      {tab === 'legal' && (
        <AdminLegalPages showToast={showToast} />
      )}

      {/* Email Log Tab */}
      {tab === 'email_log' && (
        <AdminEmailLog />
      )}

      {/* Messages Tab — founder-led contact flow */}
      {tab === 'messages' && (
        <AdminMessages onCountsChange={(counts) => setUnreadMessages(counts?.new || 0)} />
      )}

      {/* Trash Tab — recently-deleted boards */}
      {tab === 'trash' && (
        <AdminTrashBoards />
      )}

      {/* Activity Tab — system events + admin audit log */}
      {tab === 'activity' && (
        <AdminActivityTab />
      )}


      {/* Platform Tab */}
      {tab === 'platform' && (
        <div data-testid="platform-texts-panel" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '32px' }}>
          {/* Left: Form */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '28px' }}>

            {/* Home / Landing Page Section */}
            <section>
              <h3 style={sectionHeading}>Home / Landing Page</h3>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
                <FieldGroup label="Title">
                  <input data-testid="ptx-home-title" type="text" value={ptx.home_title || ''} onChange={e => updatePtxField('home_title', e.target.value)} placeholder="Welcome." style={inputStyle} />
                </FieldGroup>
                <FieldGroup label="Subtitle">
                  <textarea data-testid="ptx-home-subtitle" value={ptx.home_subtitle || ''} onChange={e => updatePtxField('home_subtitle', e.target.value)} placeholder="A calm space to organize your ideas..." style={{ ...inputStyle, minHeight: '60px', resize: 'vertical' }} />
                </FieldGroup>
                <FieldGroup label="Note (optional)">
                  <input data-testid="ptx-home-note" type="text" value={ptx.home_note || ''} onChange={e => updatePtxField('home_note', e.target.value)} placeholder="Take your time..." style={inputStyle} />
                </FieldGroup>
                <FieldGroup label="Button Text">
                  <input data-testid="ptx-home-cta" type="text" value={ptx.home_cta || ''} onChange={e => updatePtxField('home_cta', e.target.value)} placeholder="Access your workspace" style={inputStyle} />
                </FieldGroup>
                <FieldGroup label="Secondary Link Text">
                  <input data-testid="ptx-home-secondary-cta" type="text" value={ptx.home_secondary_cta || ''} onChange={e => updatePtxField('home_secondary_cta', e.target.value)} placeholder="New here? Explore Maestra" style={inputStyle} />
                </FieldGroup>
              </div>
            </section>

            {/* Login Page Section */}
            <section>
              <h3 style={sectionHeading}>Login Page</h3>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
                <FieldGroup label="Title">
                  <input data-testid="ptx-login-title" type="text" value={ptx.login_title || ''} onChange={e => updatePtxField('login_title', e.target.value)} placeholder="Welcome Back" style={inputStyle} />
                </FieldGroup>
                <FieldGroup label="Description (optional)">
                  <input data-testid="ptx-login-description" type="text" value={ptx.login_description || ''} onChange={e => updatePtxField('login_description', e.target.value)} placeholder="Sign in to access your workspace" style={inputStyle} />
                </FieldGroup>
              </div>
            </section>

            {/* Invite Page Section */}
            <section>
              <h3 style={sectionHeading}>Invite Page</h3>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
                <FieldGroup label="Title">
                  <input data-testid="ptx-invite-title" type="text" value={ptx.invite_title || ''} onChange={e => updatePtxField('invite_title', e.target.value)} placeholder="Set Up Your Account" style={inputStyle} />
                </FieldGroup>
                <FieldGroup label="Description">
                  <input data-testid="ptx-invite-description" type="text" value={ptx.invite_description || ''} onChange={e => updatePtxField('invite_description', e.target.value)} placeholder="Welcome! Complete your account to get started." style={inputStyle} />
                </FieldGroup>
              </div>
            </section>

            {/* Request Access Page Section */}
            <section>
              <h3 style={sectionHeading}>Request Access Page</h3>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
                <FieldGroup label="Title">
                  <input data-testid="ptx-request-access-title" type="text" value={ptx.request_access_title || ''} onChange={e => updatePtxField('request_access_title', e.target.value)} placeholder="Request Access" style={inputStyle} />
                </FieldGroup>
                <FieldGroup label="Description">
                  <textarea data-testid="ptx-request-access-description" value={ptx.request_access_description || ''} onChange={e => updatePtxField('request_access_description', e.target.value)} placeholder="Maestra is a private platform..." style={{ ...inputStyle, minHeight: '60px', resize: 'vertical' }} />
                </FieldGroup>
                <FieldGroup label="Submit Button Text">
                  <input data-testid="ptx-request-access-cta" type="text" value={ptx.request_access_cta_label || ''} onChange={e => updatePtxField('request_access_cta_label', e.target.value)} placeholder="Submit Request" style={inputStyle} />
                </FieldGroup>
                <FieldGroup label="Success Message">
                  <textarea data-testid="ptx-request-access-success" value={ptx.request_access_success_message || ''} onChange={e => updatePtxField('request_access_success_message', e.target.value)} placeholder="Thank you. Your request has been received..." style={{ ...inputStyle, minHeight: '60px', resize: 'vertical' }} />
                </FieldGroup>

                {/* Audience Tags */}
                <div>
                  <label style={{ display: 'block', fontSize: '11px', color: 'var(--kahla-text-light)', marginBottom: '8px', letterSpacing: '0.03em' }}>
                    "Who Maestra is for" Tags
                  </label>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                    {(ptx.request_access_audience_tags || []).map((tag, idx) => (
                      <div key={idx} style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
                        <input
                          data-testid={`ptx-audience-tag-${idx}`}
                          type="text"
                          value={tag}
                          onChange={e => {
                            const tags = [...(ptx.request_access_audience_tags || [])];
                            tags[idx] = e.target.value;
                            updatePtxField('request_access_audience_tags', tags);
                          }}
                          style={{ ...inputStyle, flex: 1 }}
                        />
                        <button
                          data-testid={`ptx-audience-tag-remove-${idx}`}
                          onClick={() => {
                            const tags = (ptx.request_access_audience_tags || []).filter((_, i) => i !== idx);
                            updatePtxField('request_access_audience_tags', tags);
                          }}
                          style={{
                            padding: '6px 10px',
                            fontSize: '14px',
                            border: '1px solid var(--kahla-border)',
                            background: 'transparent',
                            color: 'var(--kahla-text-light)',
                            cursor: 'pointer',
                            fontFamily: 'inherit',
                            lineHeight: 1,
                          }}
                          title="Remove"
                        >&times;</button>
                      </div>
                    ))}
                    <button
                      data-testid="ptx-audience-tag-add"
                      onClick={() => {
                        const tags = [...(ptx.request_access_audience_tags || []), ''];
                        updatePtxField('request_access_audience_tags', tags);
                      }}
                      style={{
                        padding: '6px 12px',
                        fontSize: '11px',
                        letterSpacing: '0.04em',
                        border: '1px dashed var(--kahla-border)',
                        background: 'transparent',
                        color: 'var(--kahla-text-light)',
                        cursor: 'pointer',
                        fontFamily: 'inherit',
                        alignSelf: 'flex-start',
                      }}
                    >+ Add tag</button>
                  </div>
                </div>

                {/* Benefits Cards */}
                <div>
                  <label style={{ display: 'block', fontSize: '11px', color: 'var(--kahla-text-light)', marginBottom: '8px', letterSpacing: '0.03em' }}>
                    "What Maestra helps with" Cards
                  </label>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                    {(ptx.request_access_benefits || []).map((item, idx) => (
                      <div key={idx} style={{
                        padding: '12px',
                        border: '1px solid var(--kahla-border)',
                        background: 'var(--kahla-cream, #faf8f5)',
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '6px',
                      }}>
                        <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
                          <span style={{ fontSize: '10px', color: 'var(--kahla-text-light)', minWidth: '20px' }}>#{idx + 1}</span>
                          <input
                            data-testid={`ptx-benefit-title-${idx}`}
                            type="text"
                            value={item.title || ''}
                            onChange={e => {
                              const items = [...(ptx.request_access_benefits || [])];
                              items[idx] = { ...items[idx], title: e.target.value };
                              updatePtxField('request_access_benefits', items);
                            }}
                            placeholder="Title"
                            style={{ ...inputStyle, flex: 1, fontWeight: '600', fontSize: '12px' }}
                          />
                          <button
                            data-testid={`ptx-benefit-remove-${idx}`}
                            onClick={() => {
                              const items = (ptx.request_access_benefits || []).filter((_, i) => i !== idx);
                              updatePtxField('request_access_benefits', items);
                            }}
                            style={{
                              padding: '4px 8px',
                              fontSize: '14px',
                              border: '1px solid var(--kahla-border)',
                              background: 'transparent',
                              color: 'var(--kahla-text-light)',
                              cursor: 'pointer',
                              fontFamily: 'inherit',
                              lineHeight: 1,
                            }}
                            title="Remove"
                          >&times;</button>
                        </div>
                        <textarea
                          data-testid={`ptx-benefit-desc-${idx}`}
                          value={item.desc || ''}
                          onChange={e => {
                            const items = [...(ptx.request_access_benefits || [])];
                            items[idx] = { ...items[idx], desc: e.target.value };
                            updatePtxField('request_access_benefits', items);
                          }}
                          placeholder="Description"
                          style={{ ...inputStyle, minHeight: '40px', resize: 'vertical', fontSize: '12px' }}
                        />
                      </div>
                    ))}
                    <button
                      data-testid="ptx-benefit-add"
                      onClick={() => {
                        const items = [...(ptx.request_access_benefits || []), { title: '', desc: '' }];
                        updatePtxField('request_access_benefits', items);
                      }}
                      style={{
                        padding: '6px 12px',
                        fontSize: '11px',
                        letterSpacing: '0.04em',
                        border: '1px dashed var(--kahla-border)',
                        background: 'transparent',
                        color: 'var(--kahla-text-light)',
                        cursor: 'pointer',
                        fontFamily: 'inherit',
                        alignSelf: 'flex-start',
                      }}
                    >+ Add card</button>
                  </div>
                </div>
              </div>
            </section>

            {/* Footer Section */}
            <section>
              <h3 style={sectionHeading}>System Footer</h3>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
                <FieldGroup label="Footer Text">
                  <input data-testid="ptx-footer-text" type="text" value={ptx.footer_text || ''} onChange={e => updatePtxField('footer_text', e.target.value)} placeholder="MAESTRA 2026" style={inputStyle} />
                </FieldGroup>
                <FieldGroup label="Footer Subtext">
                  <input data-testid="ptx-footer-subtext" type="text" value={ptx.footer_subtext || ''} onChange={e => updatePtxField('footer_subtext', e.target.value)} placeholder="One project. One space. One clear direction." style={inputStyle} />
                </FieldGroup>
                <FieldGroup label="Footer Link URL (optional)">
                  <input data-testid="ptx-footer-link" type="text" value={ptx.footer_link || ''} onChange={e => updatePtxField('footer_link', e.target.value)} placeholder="https://..." style={inputStyle} />
                </FieldGroup>
                <FieldGroup label="Footer Link Label">
                  <input data-testid="ptx-footer-link-label" type="text" value={ptx.footer_link_label || ''} onChange={e => updatePtxField('footer_link_label', e.target.value)} placeholder="Visit Website" style={inputStyle} />
                </FieldGroup>
              </div>
            </section>

            {/* Empty States Section */}
            <section>
              <h3 style={sectionHeading}>Empty States</h3>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
                <FieldGroup label="No Boards Message (optional)">
                  <input data-testid="ptx-empty-boards" type="text" value={ptx.empty_state_boards || ''} onChange={e => updatePtxField('empty_state_boards', e.target.value)} placeholder="Create your first board to start curating" style={inputStyle} />
                </FieldGroup>
              </div>
            </section>

            {/* PDF Export Footer Section */}
            <section>
              <h3 style={sectionHeading}>PDF Export Footer</h3>
              <p style={{ fontSize: '12px', color: 'var(--kahla-text-light)', fontStyle: 'italic', margin: '0 0 18px', lineHeight: 1.55 }}>
                These texts appear at the bottom of every exported PDF and ZIP cover sheet. Two variants are rendered automatically based on each user's plan:
                workspaces with branding <strong>OFF</strong> get the Basic footer below; workspaces with branding <strong>ON</strong> see their own brand name + only the Pro attribution.
              </p>

              <div style={{ background: 'var(--kahla-cream-light, #faf8f5)', border: '1px solid var(--kahla-border)', padding: '20px 22px', marginBottom: '18px' }}>
                <p style={{ fontFamily: 'Playfair Display, serif', fontSize: '11px', letterSpacing: '0.22em', textTransform: 'uppercase', color: 'var(--kahla-text-light)', margin: '0 0 14px' }}>
                  Basic plan footer · branding OFF
                </p>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
                  <FieldGroup label="Footer title">
                    <input data-testid="ptx-pdf-basic-title" type="text" value={ptx.pdf_footer_basic_title || ''} onChange={e => updatePtxField('pdf_footer_basic_title', e.target.value)} placeholder="Maestra | 2026" style={inputStyle} />
                  </FieldGroup>
                  <FieldGroup label="Footer subtext">
                    <input data-testid="ptx-pdf-basic-subtext" type="text" value={ptx.pdf_footer_basic_subtext || ''} onChange={e => updatePtxField('pdf_footer_basic_subtext', e.target.value)} placeholder="From inspiration to final direction — in one place." style={inputStyle} />
                  </FieldGroup>
                  <FieldGroup label="Attribution">
                    <input data-testid="ptx-pdf-basic-attribution" type="text" value={ptx.pdf_footer_basic_attribution || ''} onChange={e => updatePtxField('pdf_footer_basic_attribution', e.target.value)} placeholder="Created with Maestra" style={inputStyle} />
                  </FieldGroup>
                </div>
              </div>

              <div style={{ background: 'var(--kahla-cream-light, #faf8f5)', border: '1px solid var(--kahla-border)', padding: '20px 22px' }}>
                <p style={{ fontFamily: 'Playfair Display, serif', fontSize: '11px', letterSpacing: '0.22em', textTransform: 'uppercase', color: 'var(--kahla-text-light)', margin: '0 0 14px' }}>
                  Pro plan footer · branding ON
                </p>
                <p style={{ fontSize: '11px', color: 'var(--kahla-text-light)', fontStyle: 'italic', margin: '0 0 14px' }}>
                  Title, subtext and link are taken from each user's branding settings. You can only edit the subtle attribution line shown beneath them.
                </p>
                <FieldGroup label="Attribution">
                  <input data-testid="ptx-pdf-pro-attribution" type="text" value={ptx.pdf_footer_pro_attribution || ''} onChange={e => updatePtxField('pdf_footer_pro_attribution', e.target.value)} placeholder="Created with Maestra" style={inputStyle} />
                </FieldGroup>
              </div>
            </section>

            {/* Save */}
            <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
              <button className="btn-primary" data-testid="save-platform-texts-btn" onClick={handleSavePtx} disabled={savingPtx || !ptxDirty} style={{ opacity: (!ptxDirty) ? 0.5 : 1 }}>
                {savingPtx ? 'Saving...' : 'Save Changes'}
              </button>
              {!ptxDirty && <span style={{ fontSize: '11px', color: 'var(--kahla-text-light)' }}>All changes saved</span>}
            </div>
          </div>

          {/* Right: Live Previews */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
            <h3 style={sectionHeading}>Live Preview</h3>

            {/* Home / Landing Preview */}
            <PreviewCard label="Home / Landing Page">
              <div style={{ textAlign: 'center', padding: '28px 20px' }}>
                <div style={{ fontFamily: 'Playfair Display, serif', fontSize: '16px', letterSpacing: '0.25em', textTransform: 'uppercase', color: 'var(--kahla-text-light)', marginBottom: '20px' }}>MAESTRA</div>
                <div style={{ fontFamily: 'Playfair Display, serif', fontSize: '22px', color: 'var(--kahla-text)', marginBottom: '12px' }}>
                  {ptx.home_title || 'Welcome.'}
                </div>
                <div style={{ fontSize: '12px', color: 'var(--kahla-text-light)', lineHeight: '1.8', whiteSpace: 'pre-line', marginBottom: '12px' }}>
                  {ptx.home_subtitle || 'A calm space to organize your ideas, direction and decisions.\nEverything in one place — clear, simple, structured.'}
                </div>
                {(ptx.home_note || 'Take your time...') && (
                  <div style={{ fontSize: '11px', color: 'var(--kahla-text-light)', fontStyle: 'italic', marginBottom: '16px' }}>
                    {ptx.home_note || 'Take your time. Everything here is designed to support your process.'}
                  </div>
                )}
                <div style={{ display: 'inline-block', padding: '8px 20px', border: '1px solid var(--kahla-border)', fontSize: '11px', letterSpacing: '0.05em', color: 'var(--kahla-text)' }}>
                  {ptx.home_cta || 'Access your workspace'}
                </div>
                <div style={{ marginTop: '12px', fontSize: '10px', color: 'var(--kahla-text-light)', opacity: 0.6 }}>
                  {ptx.home_secondary_cta || 'New here? Explore Maestra'}
                </div>
              </div>
            </PreviewCard>

            {/* Login Preview */}
            <PreviewCard label="Login Page">
              <div style={{ textAlign: 'center', padding: '24px 16px' }}>
                <div style={{ fontFamily: 'Playfair Display, serif', fontSize: '18px', letterSpacing: '0.25em', textTransform: 'uppercase', color: 'var(--kahla-text-light)', marginBottom: '16px' }}>MAESTRA</div>
                <div style={{ fontFamily: 'Playfair Display, serif', fontSize: '20px', color: 'var(--kahla-text)', marginBottom: '8px' }}>
                  {ptx.login_title || 'Welcome Back'}
                </div>
                {ptx.login_description && (
                  <div style={{ fontSize: '12px', color: 'var(--kahla-text-light)' }}>{ptx.login_description}</div>
                )}
                <div style={{ marginTop: '20px', display: 'flex', flexDirection: 'column', gap: '10px', maxWidth: '200px', margin: '20px auto 0' }}>
                  <div style={{ height: '32px', background: '#f0ede8', borderRadius: '2px' }} />
                  <div style={{ height: '32px', background: '#f0ede8', borderRadius: '2px' }} />
                  <div style={{ height: '32px', background: 'var(--kahla-text)', borderRadius: '2px', opacity: 0.15 }} />
                </div>
              </div>
            </PreviewCard>

            {/* Invite Preview */}
            <PreviewCard label="Invite Page">
              <div style={{ textAlign: 'center', padding: '24px 16px' }}>
                <div style={{ fontFamily: 'Playfair Display, serif', fontSize: '18px', letterSpacing: '0.25em', textTransform: 'uppercase', color: 'var(--kahla-text-light)', marginBottom: '16px' }}>MAESTRA</div>
                <div style={{ fontFamily: 'Playfair Display, serif', fontSize: '20px', color: 'var(--kahla-text)', marginBottom: '8px' }}>
                  {ptx.invite_title || 'Set Up Your Account'}
                </div>
                <div style={{ fontSize: '12px', color: 'var(--kahla-text-light)', marginBottom: '16px' }}>
                  {ptx.invite_description || 'Welcome! Complete your account to get started.'}
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', maxWidth: '200px', margin: '0 auto' }}>
                  <div style={{ height: '28px', background: '#f0ede8', borderRadius: '2px' }} />
                  <div style={{ height: '28px', background: '#f0ede8', borderRadius: '2px' }} />
                  <div style={{ height: '28px', background: '#f0ede8', borderRadius: '2px' }} />
                  <div style={{ height: '32px', background: 'var(--kahla-text)', borderRadius: '2px', opacity: 0.15 }} />
                </div>
              </div>
            </PreviewCard>

            {/* Request Access Preview */}
            <PreviewCard label="Request Access Page">
              <div style={{ textAlign: 'center', padding: '24px 16px' }}>
                <div style={{ fontFamily: 'Playfair Display, serif', fontSize: '16px', letterSpacing: '0.25em', textTransform: 'uppercase', color: 'var(--kahla-text-light)', marginBottom: '16px' }}>MAESTRA</div>
                <div style={{ fontFamily: 'Playfair Display, serif', fontSize: '18px', color: 'var(--kahla-text)', marginBottom: '8px' }}>
                  {ptx.request_access_title || 'Request Access'}
                </div>
                <div style={{ fontSize: '11px', color: 'var(--kahla-text-light)', lineHeight: '1.7', whiteSpace: 'pre-line', marginBottom: '16px' }}>
                  {ptx.request_access_description || 'Maestra is a private platform for creative professionals.\nTell us about yourself and we\'ll review your request personally.'}
                </div>
                <div style={{ display: 'inline-block', padding: '8px 20px', background: 'var(--kahla-text)', color: 'var(--kahla-white)', fontSize: '10px', letterSpacing: '0.05em' }}>
                  {ptx.request_access_cta_label || 'Submit Request'}
                </div>

                {/* Tags preview */}
                <div style={{ marginTop: '16px', paddingTop: '12px', borderTop: '1px solid var(--kahla-border)' }}>
                  <div style={{ fontSize: '10px', color: 'var(--kahla-text-light)', marginBottom: '8px', letterSpacing: '0.04em', textTransform: 'uppercase' }}>Who it's for</div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: '4px' }}>
                    {((ptx.request_access_audience_tags?.length > 0) ? ptx.request_access_audience_tags : ['Photographers', 'Decorators', 'Wedding Planners', 'Designers', 'Creative Studios']).filter(t3 => t3.trim()).map((tag, i) => (
                      <span key={i} style={{
                        padding: '3px 8px',
                        border: '1px solid var(--kahla-border)',
                        fontSize: '9px',
                        color: 'var(--kahla-text)',
                        background: 'var(--kahla-white)',
                      }}>{tag}</span>
                    ))}
                  </div>
                </div>

                {/* Benefits preview */}
                <div style={{ marginTop: '12px', paddingTop: '12px', borderTop: '1px solid var(--kahla-border)' }}>
                  <div style={{ fontSize: '10px', color: 'var(--kahla-text-light)', marginBottom: '8px', letterSpacing: '0.04em', textTransform: 'uppercase' }}>What it helps with</div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '6px', textAlign: 'left' }}>
                    {((ptx.request_access_benefits?.length > 0) ? ptx.request_access_benefits : [
                      { title: 'Organize inspiration', desc: '...' },
                      { title: 'Collect decisions', desc: '...' },
                      { title: 'Reduce chaos', desc: '...' },
                      { title: 'Calm workflow', desc: '...' },
                    ]).filter(b => b.title?.trim()).map((b, i) => (
                      <div key={i} style={{
                        padding: '6px 8px',
                        border: '1px solid var(--kahla-border)',
                        background: 'var(--kahla-white)',
                      }}>
                        <div style={{ fontSize: '9px', fontWeight: '600', color: 'var(--kahla-text)' }}>{b.title}</div>
                        <div style={{ fontSize: '8px', color: 'var(--kahla-text-light)', marginTop: '2px', lineHeight: '1.4' }}>{b.desc}</div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </PreviewCard>

            {/* Footer Everywhere Preview — single source of truth, three surfaces */}
            <PreviewCard label="Footer everywhere">
              <p style={{
                margin: '0 0 14px',
                fontSize: 11,
                color: 'var(--kahla-text-light)',
                fontStyle: 'italic',
                lineHeight: 1.55,
              }}>
                Live preview of how the footer renders across Maestra. Same source on every surface — edit any field above and watch all three update together.
              </p>

              {/* 1 · Auth page */}
              <div style={footerScene}>
                <div style={footerSceneEyebrow}>Auth page</div>
                <div style={footerSceneBody}>
                  <div style={{ fontFamily: 'Playfair Display, serif', fontSize: 14, color: 'var(--kahla-charcoal)', marginBottom: 22, opacity: 0.6 }}>
                    Reset access
                  </div>
                  <div style={footerSceneFooterBand}>
                    <div style={footerSceneText}>{ptx.footer_text || 'MAESTRA 2026'}</div>
                    <div style={footerSceneSubtext}>{ptx.footer_subtext || 'One project. One space. One clear direction.'}</div>
                    {ptx.footer_link && ptx.footer_link_label && (
                      <div style={footerSceneLink}>{ptx.footer_link_label}</div>
                    )}
                  </div>
                </div>
              </div>

              {/* 2 · Client expired board view */}
              <div style={footerScene}>
                <div style={footerSceneEyebrow}>Client board · expired view</div>
                <div style={{ ...footerSceneBody, background: '#faf8f5' }}>
                  <div style={{ ...footerSceneText, fontSize: 9, letterSpacing: '0.3em', textTransform: 'uppercase', marginBottom: 24 }}>
                    {ptx.footer_text || 'MAESTRA 2026'}
                  </div>
                  <div style={{ fontFamily: 'Playfair Display, serif', fontSize: 13, color: 'var(--kahla-charcoal)', marginBottom: 6, opacity: 0.75 }}>
                    This moodboard is no longer active.
                  </div>
                  <div style={{ fontSize: 9, color: 'var(--kahla-text-light)', marginBottom: 22, fontStyle: 'italic', opacity: 0.7 }}>
                    Thank you for sharing your feeling.
                  </div>
                  <div style={footerSceneFooterBand}>
                    <div style={footerSceneText}>{ptx.footer_text || 'MAESTRA 2026'}</div>
                    <div style={footerSceneSubtext}>{ptx.footer_subtext || 'One project. One space. One clear direction.'}</div>
                  </div>
                </div>
              </div>

              {/* 3 · PDF basic-plan footer band */}
              <div style={footerScene}>
                <div style={footerSceneEyebrow}>PDF · Basic plan footer</div>
                <div style={{ ...footerSceneBody, padding: '22px 16px 16px' }}>
                  <div style={{ fontSize: 8, letterSpacing: '0.12em', color: 'var(--kahla-text-light)', opacity: 0.55, marginBottom: 14 }}>
                    … board content above …
                  </div>
                  <div style={{
                    borderTop: '1px solid var(--kahla-border)',
                    paddingTop: 12,
                    textAlign: 'center',
                  }}>
                    <div style={{ fontFamily: 'Playfair Display, serif', fontSize: 11, letterSpacing: '0.15em', color: 'var(--kahla-charcoal)' }}>
                      {ptx.pdf_footer_basic_title || ptx.footer_text || 'MAESTRA 2026'}
                    </div>
                    <div style={{ fontSize: 9, letterSpacing: '0.08em', color: 'var(--kahla-text-light)', marginTop: 3, opacity: 0.8 }}>
                      {ptx.pdf_footer_basic_subtext || ptx.footer_subtext || 'One project. One space. One clear direction.'}
                    </div>
                    <div style={{ fontSize: 8, letterSpacing: '0.22em', textTransform: 'uppercase', color: 'var(--kahla-text-light)', marginTop: 8, opacity: 0.55 }}>
                      {ptx.pdf_footer_basic_attribution || 'Created with Maestra'}
                    </div>
                  </div>
                </div>
              </div>
            </PreviewCard>

            {/* Empty State Preview */}
            <PreviewCard label="Empty Board State">
              <div style={{ textAlign: 'center', padding: '24px 16px' }}>
                <div style={{ fontSize: '28px', marginBottom: '12px', opacity: 0.3 }}>&#x1F4CB;</div>
                <div style={{ fontFamily: 'Playfair Display, serif', fontSize: '14px', color: 'var(--kahla-text)', marginBottom: '6px' }}>No Boards Yet</div>
                <div style={{ fontSize: '12px', color: 'var(--kahla-text-light)' }}>
                  {ptx.empty_state_boards || 'Create your first board to start curating'}
                </div>
              </div>
            </PreviewCard>
          </div>
        </div>
      )}

      {/* Invite Modal */}
      {showInvite && (
        <Modal onClose={() => setShowInvite(false)} title={t.invite_user || 'Invite User'}>
          {!inviteResult ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              <FieldGroup label="Email">
                <input
                  data-testid="invite-email"
                  type="email"
                  value={inviteEmail}
                  onChange={e => setInviteEmail(e.target.value)}
                  placeholder="user@example.com"
                  style={inputStyle}
                />
              </FieldGroup>
              <FieldGroup label="Username (optional)">
                <input
                  data-testid="invite-username"
                  type="text"
                  value={inviteUsername}
                  onChange={e => setInviteUsername(e.target.value)}
                  placeholder="Username"
                  style={inputStyle}
                />
              </FieldGroup>
              <FieldGroup label={t.max_storage_mb || 'Max Storage (MB)'}>
                <input
                  data-testid="invite-storage"
                  type="number"
                  value={inviteStorage}
                  onChange={e => setInviteStorage(parseInt(e.target.value) || 500)}
                  style={inputStyle}
                />
              </FieldGroup>
              <FieldGroup label={t.max_boards_limit || 'Max Boards (empty = unlimited)'}>
                <input
                  data-testid="invite-max-boards"
                  type="number"
                  value={inviteMaxBoards}
                  onChange={e => setInviteMaxBoards(e.target.value)}
                  placeholder={t.unlimited || 'Unlimited'}
                  style={inputStyle}
                />
              </FieldGroup>
              <FieldGroup label={t.templates || 'Template'}>
                <select
                  data-testid="invite-template"
                  value={inviteTemplateId}
                  onChange={e => setInviteTemplateId(e.target.value)}
                  style={inputStyle}
                >
                  <option value="">None</option>
                  {templates.map(tmpl => (
                    <option key={tmpl.id} value={tmpl.id}>{tmpl.name}</option>
                  ))}
                </select>
              </FieldGroup>
              <button className="btn-primary" data-testid="send-invite-btn" onClick={handleInvite} style={{ marginTop: '8px' }}>
                {t.invite_user || 'Create Invite'}
              </button>
            </div>
          ) : (
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: '14px', color: 'var(--kahla-text)', marginBottom: '16px' }}>
                Invite created for <strong>{inviteResult.email}</strong>
              </div>
              <div style={{
                background: '#f8f6f3',
                padding: '12px',
                borderRadius: '4px',
                fontSize: '12px',
                wordBreak: 'break-all',
                marginBottom: '16px',
                border: '1px solid var(--kahla-border)',
              }}>
                {window.location.origin}{inviteResult.invite_url}
              </div>
              <button
                className="btn-primary"
                data-testid="copy-new-invite-link"
                onClick={() => copyInviteLink(inviteResult.invite_url)}
                style={{ width: '100%' }}
              >
                {t.copy_invite_link || 'Copy Invite Link'}
              </button>
              <div style={{ fontSize: '11px', color: 'var(--kahla-text-light)', marginTop: '12px' }}>
                Expires: {new Date(inviteResult.expires_at).toLocaleDateString()}
              </div>
            </div>
          )}
        </Modal>
      )}

      {/* Limits Modal */}
      {showLimits && (
        <Modal onClose={() => setShowLimits(null)} title={t.set_limits || 'Set Limits'}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            <FieldGroup label={t.max_storage_mb || 'Max Storage (MB)'}>
              <input
                data-testid="limit-storage"
                type="number"
                value={limitStorage}
                onChange={e => setLimitStorage(parseInt(e.target.value) || 0)}
                style={inputStyle}
              />
            </FieldGroup>
            <FieldGroup label={t.max_boards_limit || 'Max Boards (empty = unlimited)'}>
              <input
                data-testid="limit-max-boards"
                type="number"
                value={limitMaxBoards}
                onChange={e => setLimitMaxBoards(e.target.value)}
                placeholder={t.unlimited || 'Unlimited'}
                style={inputStyle}
              />
            </FieldGroup>
            <button className="btn-primary" data-testid="save-limits-btn" onClick={handleLimitsSubmit}>
              {t.save || 'Save'}
            </button>
          </div>
        </Modal>
      )}

      {/* Template Create Modal */}
      {showTemplateModal && (
        <Modal onClose={() => setShowTemplateModal(false)} title={t.create_template || 'Create Template'}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            <FieldGroup label={t.template_name || 'Template Name'}>
              <input
                data-testid="template-name-input"
                type="text"
                value={templateName}
                onChange={e => setTemplateName(e.target.value)}
                placeholder="My Template"
                style={inputStyle}
              />
            </FieldGroup>
            <FieldGroup label={t.default_labels || 'Labels (comma separated)'}>
              <input
                data-testid="template-labels-input"
                type="text"
                value={templateLabels}
                onChange={e => setTemplateLabels(e.target.value)}
                style={inputStyle}
              />
            </FieldGroup>
            <FieldGroup label={t.client_language || 'Client Language'}>
              <select
                data-testid="template-lang-select"
                value={templateLang}
                onChange={e => setTemplateLang(e.target.value)}
                style={inputStyle}
              >
                {LANGUAGE_OPTIONS.map(o => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
            </FieldGroup>
            <div style={{
              padding: '12px 14px',
              background: '#faf8f5',
              border: '1px dashed var(--kahla-border)',
              fontSize: '12px', color: 'var(--kahla-text-light)',
              fontStyle: 'italic', lineHeight: 1.55,
            }} data-testid="template-features-moved-notice">
              Feature access lives with Plans now. Pick a Plan in the user&rsquo;s drawer to decide which features each workspace unlocks.
            </div>
            <button className="btn-primary" data-testid="save-template-btn" onClick={handleCreateTemplate} style={{ marginTop: '8px' }}>
              {t.create_template || 'Create'}
            </button>
          </div>
        </Modal>
      )}

      {/* Deleted Users Section */}
      {tab === 'users' && deletedUsers.length > 0 && (
        <div style={{ marginTop: '32px', borderTop: '1px solid var(--kahla-border)', paddingTop: '24px' }}>
          <h3 style={{ fontSize: '13px', textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--kahla-text-light)', marginBottom: '16px' }}>
            Deleted Users ({deletedUsers.length})
          </h3>
          <div style={{ display: 'grid', gap: '8px' }}>
            {deletedUsers.map(du => (
              <div key={du.id} data-testid={`deleted-user-${du.username}`} style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                padding: '12px 16px',
                background: '#fafafa',
                border: '1px solid var(--kahla-border)',
                fontSize: '13px',
              }}>
                <div style={{ display: 'flex', gap: '16px', alignItems: 'center' }}>
                  <span style={{ fontWeight: '500' }}>{du.username}</span>
                  <span style={{ color: 'var(--kahla-text-light)', fontSize: '12px' }}>{du.email}</span>
                  <span style={{ fontSize: '11px', color: '#e65100' }}>
                    {du.days_remaining} days until permanent deletion
                  </span>
                  <span style={{ fontSize: '11px', color: 'var(--kahla-text-light)' }}>
                    ({du.board_count} boards, {du.library_image_count} images, {du.document_count} docs)
                  </span>
                </div>
                <div style={{ display: 'flex', gap: '8px' }}>
                  <button
                    data-testid={`restore-${du.username}`}
                    onClick={() => handleRestore(du.id)}
                    style={actionBtnStyle('#2e7d32')}
                  >
                    Restore
                  </button>
                  <button
                    data-testid={`permanent-delete-${du.username}`}
                    onClick={() => setPermanentDeleteTarget(du)}
                    style={actionBtnStyle('#c62828')}
                  >
                    Delete Permanently
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deleteTarget && (
        <Modal onClose={() => setDeleteTarget(null)} title="Delete User">
          <div style={{ textAlign: 'center' }}>
            <div style={{
              width: '48px',
              height: '48px',
              borderRadius: '50%',
              background: '#ffebee',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 16px',
              fontSize: '20px',
              color: '#c62828',
            }}>!</div>
            <p style={{ fontSize: '14px', color: 'var(--kahla-text)', marginBottom: '8px' }}>
              Delete <strong>{deleteTarget.username}</strong>?
            </p>
            <p style={{ fontSize: '12px', color: 'var(--kahla-text-light)', marginBottom: '24px', lineHeight: '1.5' }}>
              This will mark the user as deleted. Their data (boards, images, documents) will be retained for 30 days and can be restored during that period.
            </p>
            <div style={{ display: 'flex', gap: '12px', justifyContent: 'center' }}>
              <button
                className="btn-secondary"
                onClick={() => setDeleteTarget(null)}
                style={{ minWidth: '100px' }}
              >
                Cancel
              </button>
              <button
                data-testid="confirm-delete-btn"
                onClick={handleSoftDelete}
                style={{
                  padding: '10px 24px',
                  background: '#c62828',
                  color: '#fff',
                  border: 'none',
                  cursor: 'pointer',
                  fontFamily: 'inherit',
                  fontSize: '13px',
                  minWidth: '100px',
                }}
              >
                Delete User
              </button>
            </div>
          </div>
        </Modal>
      )}

      {/* Permanent Delete Confirmation Modal */}
      {permanentDeleteTarget && (
        <Modal onClose={() => setPermanentDeleteTarget(null)} title="Permanent Deletion">
          <div style={{ textAlign: 'center' }}>
            <div style={{
              width: '48px',
              height: '48px',
              borderRadius: '50%',
              background: '#c62828',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 16px',
              fontSize: '20px',
              color: '#fff',
              fontWeight: 'bold',
            }}>!!</div>
            <p style={{ fontSize: '14px', color: 'var(--kahla-text)', marginBottom: '8px', fontWeight: '600' }}>
              Permanently delete {permanentDeleteTarget.username}?
            </p>
            <p style={{ fontSize: '12px', color: '#c62828', marginBottom: '24px', lineHeight: '1.5', fontWeight: '500' }}>
              This will permanently delete all user data (boards, images, documents, notes, labels). This action cannot be undone.
            </p>
            <div style={{ display: 'flex', gap: '12px', justifyContent: 'center' }}>
              <button
                className="btn-secondary"
                onClick={() => setPermanentDeleteTarget(null)}
                style={{ minWidth: '100px' }}
              >
                Cancel
              </button>
              <button
                data-testid="confirm-permanent-delete-btn"
                onClick={handlePermanentDelete}
                style={{
                  padding: '10px 24px',
                  background: '#c62828',
                  color: '#fff',
                  border: 'none',
                  cursor: 'pointer',
                  fontFamily: 'inherit',
                  fontSize: '13px',
                  minWidth: '100px',
                }}
              >
                Delete Permanently
              </button>
            </div>
          </div>
        </Modal>
      )}

      {/* Manage Access Modal */}
      {accessTarget && (
        <ManageAccessModal
          user={accessTarget}
          templates={templates}
          onClose={() => setAccessTarget(null)}
          onAction={() => { fetchAll({ silent: true }); }}
          showToast={showToast}
        />
      )}

      {/* User Details Drawer — Plan + Feature Overrides (Phase 2 of the
          Templates/Plans/User split). Calm right-side drawer, never a modal.
          Re-resolves the current user from `users` so changes after refetch
          stay live without remounting. */}
      {userDrawer && (
        <UserDetailsDrawer
          user={users.find(u => u.id === userDrawer) || null}
          templates={templates}
          plans={plans}
          featureKeys={featureKeys}
          onClose={() => setUserDrawer(null)}
          onAction={() => { fetchAll({ silent: true }); }}
          showToast={showToast}
        />
      )}

      {toast && <div className="toast">{toast}</div>}
    </div>
  );
};

// ==================== UI Helpers ====================

const thStyle = { padding: '10px 12px', fontSize: '11px', letterSpacing: '0.05em', textTransform: 'uppercase', color: 'var(--kahla-text-light)', fontWeight: '500' };
const tdStyle = { padding: '12px', verticalAlign: 'middle' };
const inputStyle = {
  width: '100%',
  padding: '10px 12px',
  fontSize: '13px',
  border: '1px solid var(--kahla-border)',
  background: 'var(--kahla-white)',
  fontFamily: 'inherit',
  boxSizing: 'border-box',
};
const actionBtnStyle = (color) => ({
  padding: '4px 10px',
  fontSize: '11px',
  border: `1px solid ${color}20`,
  background: `${color}08`,
  color,
  cursor: 'pointer',
  fontFamily: 'inherit',
  borderRadius: '2px',
});

const TemplateSelector = ({ userId, currentTemplateId, templates, onChanged, showToast }) => {
  const [changing, setChanging] = useState(false);
  const [confirming, setConfirming] = useState(null);

  const current = templates.find(t => t.id === currentTemplateId);
  const currentName = current?.workspace_name || '—';

  const handleChange = async (templateId) => {
    setChanging(true);
    try {
      await adminAPI.reassignUserTemplate(userId, templateId);
      showToast('Template applied');
      onChanged();
    } catch (err) {
      showToast(err.response?.data?.detail || 'Failed');
    } finally {
      setChanging(false);
      setConfirming(null);
    }
  };

  if (confirming !== null) {
    const tpl = templates.find(t => t.id === confirming);
    return (
      <div data-testid={`template-confirm-${userId}`} style={{
        display: 'flex',
        flexDirection: 'column',
        gap: '6px',
        fontSize: '11px',
      }}>
        <span style={{ color: 'var(--kahla-text-light)' }}>
          Apply <strong>{tpl?.workspace_name || 'None'}</strong>?
        </span>
        <span style={{ fontSize: '10px', color: 'var(--kahla-text-light)', opacity: 0.8 }}>
          This will update workspace configuration.
        </span>
        <div style={{ display: 'flex', gap: '4px' }}>
          <button
            data-testid={`template-confirm-yes-${userId}`}
            disabled={changing}
            onClick={() => handleChange(confirming)}
            style={{ ...actionBtnStyle('#2e7d32'), fontSize: '10px' }}
          >{changing ? '...' : 'Apply'}</button>
          <button
            onClick={() => setConfirming(null)}
            style={{ ...actionBtnStyle('#666'), fontSize: '10px' }}
          >Cancel</button>
        </div>
      </div>
    );
  }

  return (
    <select
      data-testid={`template-select-${userId}`}
      value={currentTemplateId || ''}
      onChange={e => setConfirming(e.target.value)}
      style={{
        padding: '4px 6px',
        fontSize: '11px',
        border: '1px solid var(--kahla-border)',
        background: 'var(--kahla-white)',
        color: 'var(--kahla-text)',
        fontFamily: 'inherit',
        cursor: 'pointer',
        maxWidth: '130px',
      }}
    >
      <option value="">No template</option>
      {templates.filter(t => t.workspace_name?.trim()).map(t => (
        <option key={t.id} value={t.id}>{t.workspace_name}</option>
      ))}
    </select>
  );
};

const RequestStatusBadge = ({ status }) => {
  const colors = {
    pending: { bg: '#fff8e1', color: '#f57f17' },
    approved: { bg: '#e8f5e9', color: '#2e7d32' },
    declined: { bg: '#ffebee', color: '#c62828' },
    archived: { bg: '#fafafa', color: '#9e9e9e' },
  };
  const c = colors[status] || colors.pending;
  return (
    <span style={{
      fontSize: '10px',
      padding: '2px 8px',
      background: c.bg,
      color: c.color,
      borderRadius: '2px',
      textTransform: 'uppercase',
      letterSpacing: '0.05em',
    }}>{status}</span>
  );
};

const formatStorage = (mb) => {
  if (mb >= 1024) return `${(mb / 1024).toFixed(1)} GB`;
  return `${mb.toFixed(1)} MB`;
};

const ACCESS_STATUS_STYLES = {
  admin: { bg: '#f0e6d3', color: '#5d4037', label: 'Admin' },
  trial: { bg: '#e3f2fd', color: '#1565c0', label: 'Trial' },
  active: { bg: '#e8f5e9', color: '#2e7d32', label: 'Active' },
  expired_read_only: { bg: '#fff8e1', color: '#e65100', label: 'Expired (R/O)' },
  archived_frozen: { bg: '#efebe9', color: '#4e342e', label: 'Archived' },
  invited: { bg: '#fff8e1', color: '#f57f17', label: 'Invited' },
  deactivated: { bg: '#fafafa', color: '#9e9e9e', label: 'Deactivated' },
};

const AccessStatusBadge = ({ status }) => {
  const s = ACCESS_STATUS_STYLES[status] || ACCESS_STATUS_STYLES.active;
  return (
    <span data-testid={`access-status-${status}`} style={{
      fontSize: '10px',
      padding: '2px 8px',
      background: s.bg,
      color: s.color,
      borderRadius: '2px',
      textTransform: 'uppercase',
      letterSpacing: '0.05em',
      fontWeight: '500',
    }}>{s.label}</span>
  );
};

const fmtDate = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
  } catch { return '—'; }
};

const AccessDates = ({ user }) => {
  if (user.role === 'admin') return <span style={{ fontSize: '11px', color: 'var(--kahla-text-light)', fontStyle: 'italic' }}>—</span>;
  return (
    <div style={{ fontSize: '10px', color: 'var(--kahla-text-light)', lineHeight: '1.6' }}>
      <div>Created: {fmtDate(user.created_at)}</div>
      {user.trial_ends_at && <div>Trial until: {fmtDate(user.trial_ends_at)}</div>}
      {user.access_expires_at && <div>Access until: {fmtDate(user.access_expires_at)}</div>}
      {user.days_left != null && (
        <div style={{
          fontWeight: '600',
          color: user.access_status === 'expired_read_only' ? '#e65100'
            : user.days_left <= 3 ? '#c62828'
            : user.days_left <= 7 ? '#e65100'
            : 'var(--kahla-text)',
          fontSize: '11px',
          marginTop: '2px',
        }}>
          {user.access_status === 'expired_read_only'
            ? `${user.days_left}d until archived`
            : user.days_left === 0 ? 'Expires today' : `${user.days_left}d left`
          }
        </div>
      )}
    </div>
  );
};

const UserAccessPeriod = ({ user }) => {
  if (user.role === 'admin') return <span style={{ fontSize: '11px', color: 'var(--kahla-text-light)', fontStyle: 'italic' }}>Unlimited</span>;
  if (user.access_status === 'invited') return <span style={{ fontSize: '11px', color: '#f57f17' }}>Pending activation</span>;

  const expDate = user.access_expires_at || user.trial_ends_at;
  const dl = user.days_left;

  let color = 'var(--kahla-text)';
  let daysText = '';
  if (user.access_status === 'expired_read_only' || user.access_status === 'archived_frozen') {
    color = '#c62828';
    daysText = user.access_status === 'expired_read_only' ? `${dl != null ? dl : 0}d until archived` : 'Archived';
  } else if (dl != null) {
    if (dl <= 3) color = '#c62828';
    else if (dl <= 7) color = '#e65100';
    else color = '#2e7d32';
    daysText = dl === 0 ? 'Expires today' : `${dl} days left`;
  }

  return (
    <div style={{ lineHeight: '1.5' }}>
      {expDate && (
        <div style={{ fontSize: '12px', color: 'var(--kahla-text)' }}>
          {user.trial_ends_at && !user.access_expires_at ? 'Trial until' : 'Access until'}: {fmtDate(expDate)}
        </div>
      )}
      {daysText && (
        <div style={{ fontSize: '12px', fontWeight: '600', color }}>{daysText}</div>
      )}
      {!expDate && !daysText && (
        <span style={{ fontSize: '11px', color: 'var(--kahla-text-light)' }}>No limit set</span>
      )}
      {user.archive_exports && (
        <div
          data-testid={`archive-exports-${user.username}`}
          style={{
            marginTop: 6,
            fontSize: 11,
            color: 'var(--kahla-text-light)',
            fontFamily: 'Playfair Display, serif',
            letterSpacing: '0.04em',
          }}
          title={`Total archive-mode exports since expiry.\nLimit: ${user.archive_exports.pdf_limit_per_board} PDF · ${user.archive_exports.zip_limit_per_board} ZIP per board.\nResets automatically on renewal.`}
        >
          Archive exports:&nbsp;
          <span style={{ color: 'var(--kahla-charcoal)' }}>
            {user.archive_exports.pdf_used} PDF · {user.archive_exports.zip_used} ZIP
          </span>
          {user.archive_exports.boards_touched ? (
            <span style={{ color: 'var(--kahla-text-light)' }}>
              {' '}across {user.archive_exports.boards_touched} board{user.archive_exports.boards_touched === 1 ? '' : 's'}
            </span>
          ) : null}
          <span style={{ color: 'var(--kahla-text-light)', fontStyle: 'italic' }}>
            {' '}(limit {user.archive_exports.pdf_limit_per_board}/{user.archive_exports.zip_limit_per_board} per board)
          </span>
        </div>
      )}
    </div>
  );
};

const UserActionsCell = ({ user, onManage, onOpenDrawer, onAction, showToast, t, copyInviteLink, setDeleteTarget, handleStatusChange, openDropdown, setOpenDropdown }) => {
  const isOpen = openDropdown === user.id;

  return (
    <div style={{ display: 'flex', gap: '6px', alignItems: 'center', position: 'relative' }}>
      {user.status === 'invited' && (
        <button data-testid={`copy-invite-${user.username}`} onClick={() => copyInviteLink(`/invite/${user.invite_token || ''}`)} style={actionBtnStyle('#1565c0')}>Copy Link</button>
      )}
      <button data-testid={`details-drawer-${user.username}`} onClick={onOpenDrawer} style={{ ...actionBtnStyle('#4a3e2f'), fontWeight: '600' }}>Plan</button>
      <button data-testid={`manage-access-${user.username}`} onClick={onManage} style={{ ...actionBtnStyle('#5d4037'), fontWeight: '600' }}>Manage</button>
      <button
        data-testid={`actions-menu-${user.username}`}
        onClick={() => setOpenDropdown(isOpen ? null : user.id)}
        style={{
          padding: '4px 8px', fontSize: '14px', border: '1px solid var(--kahla-border)',
          background: isOpen ? '#faf8f5' : 'transparent', color: 'var(--kahla-text-light)',
          cursor: 'pointer', fontFamily: 'inherit', lineHeight: 1,
        }}
      >&#8942;</button>
      {isOpen && (
        <div data-testid={`actions-dropdown-${user.username}`} style={{
          position: 'absolute', top: '100%', right: 0, zIndex: 50,
          background: 'var(--kahla-white)', border: '1px solid var(--kahla-border)',
          minWidth: '160px', boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
        }}>
          {(user.access_status === 'trial' || user.access_status === 'active' || user.access_status === 'expired_read_only') && (
            <button data-testid={`dropdown-extend-${user.username}`} onClick={async () => { try { await adminAPI.updateUserAccess(user.id, 'extend_access', 30); onAction(); showToast('Extended +30 days'); } catch { showToast('Failed'); } setOpenDropdown(null); }} style={dropdownItemStyle}>Extend +30 days</button>
          )}
          {(user.access_status === 'trial' || user.access_status === 'active') && (
            <button data-testid={`dropdown-expire-${user.username}`} onClick={async () => { try { await adminAPI.updateUserAccess(user.id, 'mark_expired'); onAction(); showToast('Marked expired'); } catch { showToast('Failed'); } setOpenDropdown(null); }} style={dropdownItemStyle}>Expire access</button>
          )}
          {user.status === 'active' && (
            <button data-testid={`dropdown-deactivate-${user.username}`} onClick={() => { handleStatusChange(user.id, 'deactivated'); setOpenDropdown(null); }} style={dropdownItemStyle}>Deactivate</button>
          )}
          {user.status === 'deactivated' && (
            <button data-testid={`dropdown-activate-${user.username}`} onClick={() => { handleStatusChange(user.id, 'active'); setOpenDropdown(null); }} style={dropdownItemStyle}>Activate</button>
          )}
          {(user.access_status === 'expired_read_only' || user.access_status === 'archived_frozen' || user.access_status === 'deactivated') && (
            <button data-testid={`dropdown-restore-${user.username}`} onClick={async () => { try { await adminAPI.updateUserAccess(user.id, 'restore_access', 30); onAction(); showToast('Restored'); } catch { showToast('Failed'); } setOpenDropdown(null); }} style={dropdownItemStyle}>Restore access</button>
          )}
          <button data-testid={`dropdown-delete-${user.username}`} onClick={() => { setDeleteTarget(user); setOpenDropdown(null); }} style={{ ...dropdownItemStyle, color: '#c62828' }}>Delete user</button>
        </div>
      )}
    </div>
  );
};

const dropdownItemStyle = {
  display: 'block', width: '100%', padding: '8px 14px', fontSize: '12px',
  color: 'var(--kahla-text)', background: 'transparent', border: 'none',
  cursor: 'pointer', fontFamily: 'inherit', textAlign: 'left',
};

const AccessActions = ({ user, onAction, showToast, onManage }) => {
  const [busy, setBusy] = useState(false);

  const handleAccess = async (action, days) => {
    setBusy(true);
    try {
      await adminAPI.updateUserAccess(user.id, action, days);
      onAction();
      const labels = { set_trial: 'Trial set', extend_access: 'Access extended', mark_expired: 'Marked expired', restore_access: 'Access restored', archive: 'Archived', deactivate: 'Deactivated' };
      showToast(labels[action] || 'Updated');
    } catch (err) {
      showToast(err.response?.data?.detail || 'Failed');
    } finally {
      setBusy(false);
    }
  };

  const st = user.access_status;
  return (
    <>
      {(st === 'trial' || st === 'active' || st === 'expired_read_only') && (
        <button data-testid={`extend-access-${user.username}`} disabled={busy} onClick={() => handleAccess('extend_access', 30)} style={actionBtnStyle('#2e7d32')}>+30d</button>
      )}
      {(st === 'trial' || st === 'active') && (
        <button data-testid={`mark-expired-${user.username}`} disabled={busy} onClick={() => handleAccess('mark_expired')} style={actionBtnStyle('#e65100')}>Expire</button>
      )}
      {(st === 'expired_read_only') && (
        <button data-testid={`archive-${user.username}`} disabled={busy} onClick={() => handleAccess('archive')} style={actionBtnStyle('#4e342e')}>Archive</button>
      )}
      {(st === 'expired_read_only' || st === 'archived_frozen' || st === 'deactivated') && (
        <button data-testid={`restore-access-${user.username}`} disabled={busy} onClick={() => handleAccess('restore_access', 30)} style={actionBtnStyle('#2e7d32')}>Restore</button>
      )}
      <button data-testid={`manage-access-${user.username}`} onClick={onManage} style={actionBtnStyle('#5d4037')}>Manage</button>
    </>
  );
};

const ManageAccessModal = ({ user, templates, onClose, onAction, showToast }) => {
  const [busy, setBusy] = useState(false);
  const [trialDate, setTrialDate] = useState('');
  const [accessDate, setAccessDate] = useState('');
  const [boardLimit, setBoardLimit] = useState(user.max_boards != null ? String(user.max_boards) : '');
  const [storageLimit, setStorageLimit] = useState(String(user.storage_limit_mb || 500));
  const [limitsDirty, setLimitsDirty] = useState(false);
  const [savingLimits, setSavingLimits] = useState(false);
  const [canBrand, setCanBrand] = useState(!!user.can_customize_branding);
  const [savingBrand, setSavingBrand] = useState(false);
  // Local source of truth for the assigned template. The select is controlled
  // by this state so the UI updates IMMEDIATELY after a successful save, even
  // before the parent's `users` list re-loads. Initialised from props.
  const [currentTemplateId, setCurrentTemplateId] = useState(user.template_id || '');
  const [savingTemplate, setSavingTemplate] = useState(false);
  const [templateConfirm, setTemplateConfirm] = useState(null); // pending template id awaiting confirmation
  const [confirmationNote, setConfirmationNote] = useState(''); // calm inline "Workspace updated successfully."

  // Auto-clear the confirmation note after a few seconds (no lingering chrome).
  React.useEffect(() => {
    if (!confirmationNote) return;
    const id = setTimeout(() => setConfirmationNote(''), 3500);
    return () => clearTimeout(id);
  }, [confirmationNote]);

  const handleToggleBranding = async (nextValue) => {
    if (nextValue === canBrand) return; // no-op
    // Optimistic update so the pill flips instantly
    setCanBrand(nextValue);
    setSavingBrand(true);
    try {
      await adminAPI.updateBrandingPermission(user.id, nextValue);
      onAction();
      setConfirmationNote(nextValue
        ? 'Custom branding enabled.'
        : 'Custom branding disabled — saved overrides preserved.');
    } catch (err) {
      setCanBrand(!nextValue);
      showToast(err.response?.data?.detail || 'Failed');
    } finally {
      setSavingBrand(false);
    }
  };

  const applyTemplate = async (newTplId) => {
    setSavingTemplate(true);
    try {
      await adminAPI.reassignUserTemplate(user.id, newTplId);
      setCurrentTemplateId(newTplId || '');
      setConfirmationNote('Workspace updated successfully.');
      onAction(); // refresh parent list
    } catch (err) {
      showToast(err.response?.data?.detail || 'Failed');
    } finally {
      setSavingTemplate(false);
      setTemplateConfirm(null);
    }
  };

  const handleAction = async (action, days, date) => {
    setBusy(true);
    try {
      await adminAPI.updateUserAccess(user.id, action, days, date);
      onAction();
      showToast({
        set_trial: 'Trial set', set_trial_date: 'Trial date set',
        extend_access: 'Access extended', set_access_date: 'Access date set',
        mark_expired: 'Marked expired', restore_access: 'Access restored',
        archive: 'Workspace archived', deactivate: 'Account deactivated',
      }[action] || 'Updated');
      onClose();
    } catch (err) {
      showToast(err.response?.data?.detail || 'Failed');
    } finally {
      setBusy(false);
    }
  };

  const handleSaveLimits = async () => {
    setSavingLimits(true);
    try {
      await adminAPI.updateUserLimits(user.id, {
        storage_limit_mb: parseInt(storageLimit) || 500,
        max_boards: boardLimit === '' ? null : parseInt(boardLimit),
      });
      onAction();
      showToast('Limits updated');
      setLimitsDirty(false);
    } catch (err) {
      showToast(err.response?.data?.detail || 'Failed');
    } finally {
      setSavingLimits(false);
    }
  };

  const st = user.access_status;
  const s = ACCESS_STATUS_STYLES[st] || ACCESS_STATUS_STYLES.active;
  const tpl = (templates || []).find(t => t.id === currentTemplateId);
  const sectionLabel = { fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--kahla-text-light)', marginBottom: '10px', fontWeight: '500' };

  return (
    <Modal onClose={onClose} title={`Manage — ${user.username}`}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>

        {/* Overview card */}
        <div style={{ padding: '14px 16px', background: '#faf8f5', border: '1px solid var(--kahla-border)' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
            <span style={{ fontSize: '12px', color: 'var(--kahla-text-light)', letterSpacing: '0.04em' }}>Status</span>
            <span data-testid="modal-access-status" style={{ fontSize: '11px', padding: '2px 10px', background: s.bg, color: s.color, fontWeight: '500', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{s.label}</span>
          </div>
          <div style={{ fontSize: '11px', color: 'var(--kahla-text-light)', lineHeight: '1.7' }}>
            <div>Created: {fmtDate(user.created_at)}</div>
            {user.email && <div>Email: {user.email}</div>}
            {tpl && <div>Template: {tpl.name}</div>}
            {user.trial_ends_at && <div>Trial until: {fmtDate(user.trial_ends_at)}</div>}
            {user.access_expires_at && <div>Access until: {fmtDate(user.access_expires_at)}</div>}
            {user.days_left != null && <div style={{ fontWeight: '600', color: user.days_left <= 3 ? '#c62828' : 'var(--kahla-text)' }}>{user.days_left === 0 ? 'Expires today' : `${user.days_left} days left`}</div>}
          </div>
        </div>

        {/* Limits & Usage */}
        <div>
          <div style={sectionLabel}>Limits & Usage</div>
          <div style={{ padding: '14px 16px', border: '1px solid var(--kahla-border)', background: 'var(--kahla-white)' }}>
            {/* Usage display */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '16px' }}>
              <div>
                <div style={{ fontSize: '10px', color: 'var(--kahla-text-light)', letterSpacing: '0.04em', marginBottom: '4px' }}>Boards used</div>
                <div data-testid="modal-boards-usage" style={{ fontSize: '14px', fontWeight: '600', color: 'var(--kahla-text)' }}>
                  {user.board_count}{user.max_boards != null ? ` / ${user.max_boards}` : ''}
                </div>
              </div>
              <div>
                <div style={{ fontSize: '10px', color: 'var(--kahla-text-light)', letterSpacing: '0.04em', marginBottom: '4px' }}>Storage used</div>
                <div data-testid="modal-storage-usage" style={{ fontSize: '14px', fontWeight: '600', color: 'var(--kahla-text)' }}>
                  {formatStorage(user.storage_used_mb)} / {formatStorage(user.storage_limit_mb || 500)}
                </div>
              </div>
            </div>
            {/* Editable limits */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
              <div>
                <label style={{ display: 'block', fontSize: '10px', color: 'var(--kahla-text-light)', marginBottom: '4px', letterSpacing: '0.04em' }}>Board limit</label>
                <input
                  data-testid="modal-board-limit-input"
                  type="number"
                  value={boardLimit}
                  onChange={e => { setBoardLimit(e.target.value); setLimitsDirty(true); }}
                  placeholder="Unlimited"
                  min="0"
                  style={{ ...inputStyle, width: '100%', padding: '8px 10px', fontSize: '13px' }}
                />
                <div style={{ fontSize: '9px', color: 'var(--kahla-text-light)', marginTop: '2px' }}>Leave empty for unlimited</div>
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '10px', color: 'var(--kahla-text-light)', marginBottom: '4px', letterSpacing: '0.04em' }}>Storage limit (MB)</label>
                <input
                  data-testid="modal-storage-limit-input"
                  type="number"
                  value={storageLimit}
                  onChange={e => { setStorageLimit(e.target.value); setLimitsDirty(true); }}
                  min="0"
                  style={{ ...inputStyle, width: '100%', padding: '8px 10px', fontSize: '13px' }}
                />
              </div>
            </div>
            {limitsDirty && (
              <button
                data-testid="modal-save-limits-btn"
                onClick={handleSaveLimits}
                disabled={savingLimits}
                className="btn-primary"
                style={{ marginTop: '12px', padding: '8px 24px', fontSize: '12px' }}
              >{savingLimits ? 'Saving...' : 'Save limits'}</button>
            )}
          </div>
        </div>

        {/* Template */}
        <div>
          <div style={sectionLabel}>Template</div>
          <div style={{ padding: '14px 16px', border: '1px solid var(--kahla-border)', background: 'var(--kahla-white)' }}>
            <div style={{ fontSize: '12px', color: 'var(--kahla-text-light)', marginBottom: '8px' }}>
              Current: <strong data-testid="modal-current-template-name" style={{ color: 'var(--kahla-text)' }}>{tpl ? tpl.name : 'None assigned'}</strong>
              {savingTemplate && (
                <span style={{ marginLeft: '8px', fontSize: '11px', color: 'var(--kahla-text-light)', fontStyle: 'italic' }}>updating…</span>
              )}
            </div>
            <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
              <select
                data-testid="modal-template-select"
                value={currentTemplateId || ''}
                disabled={savingTemplate}
                onChange={(e) => {
                  const newTplId = e.target.value;
                  if (newTplId === (currentTemplateId || '')) return;
                  setTemplateConfirm(newTplId);
                }}
                style={{ ...inputStyle, flex: 1, padding: '8px 10px', fontSize: '13px' }}
              >
                <option value="">No template</option>
                {(templates || []).map(t => (
                  <option key={t.id} value={t.id}>{t.name}</option>
                ))}
              </select>
            </div>
            <div style={{ fontSize: '10px', color: 'var(--kahla-text-light)', marginTop: '6px' }}>
              Applies labels, features, and workspace configuration. Preserves all boards and data.
            </div>

            {templateConfirm !== null && (() => {
              const nextTpl = (templates || []).find(t => t.id === templateConfirm);
              return (
                <div
                  data-testid="modal-template-confirm"
                  style={{
                    marginTop: '12px',
                    padding: '12px 14px',
                    background: '#faf8f5',
                    border: '1px solid var(--kahla-border)',
                  }}
                >
                  <div style={{ fontSize: '12px', color: 'var(--kahla-text)', lineHeight: '1.6', marginBottom: '10px' }}>
                    Switch this workspace to <strong>{nextTpl ? nextTpl.name : 'No template'}</strong>?
                    <div style={{ fontSize: '11px', color: 'var(--kahla-text-light)', marginTop: '4px' }}>
                      Labels and features will be updated. Boards and data are preserved.
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <button
                      data-testid="modal-template-confirm-yes"
                      onClick={() => applyTemplate(templateConfirm)}
                      disabled={savingTemplate}
                      className="btn-primary"
                      style={{ padding: '6px 18px', fontSize: '12px' }}
                    >{savingTemplate ? 'Updating…' : 'Confirm switch'}</button>
                    <button
                      data-testid="modal-template-confirm-cancel"
                      onClick={() => setTemplateConfirm(null)}
                      disabled={savingTemplate}
                      style={{
                        padding: '6px 14px', fontSize: '12px',
                        background: 'transparent', border: '1px solid var(--kahla-border)',
                        color: 'var(--kahla-text-light)', cursor: 'pointer',
                      }}
                    >Cancel</button>
                  </div>
                </div>
              );
            })()}

            {confirmationNote && (
              <div
                data-testid="modal-confirmation-note"
                style={{
                  marginTop: '10px',
                  fontSize: '11px',
                  color: '#2e7d32',
                  letterSpacing: '0.03em',
                }}
              >
                {confirmationNote}
              </div>
            )}
          </div>
        </div>

        {/* Branding — explicit YES / NO selector */}
        <div>
          <div style={sectionLabel}>Custom Branding</div>
          <div
            data-testid="modal-branding-section"
            style={{
              padding: '14px 16px', border: '1px solid var(--kahla-border)',
              background: 'var(--kahla-white)',
            }}
          >
            <div style={{ fontSize: '13px', color: 'var(--kahla-text)', fontWeight: '500', marginBottom: '4px' }}>
              Can this user customize their branding?
            </div>
            <div style={{ fontSize: '11px', color: 'var(--kahla-text-light)', lineHeight: '1.55', marginBottom: '12px' }}>
              When enabled, the user can override workspace name, logo, colors and welcome texts.
              When disabled, the template defaults are shown. Any previously saved branding is preserved
              and reactivates automatically when re-enabled.
            </div>

            <div
              role="radiogroup"
              aria-label="Allow custom branding"
              style={{
                display: 'inline-flex',
                border: '1px solid var(--kahla-border)',
                background: '#faf8f5',
              }}
            >
              {[
                { value: true, label: 'Yes', testId: 'modal-branding-yes' },
                { value: false, label: 'No', testId: 'modal-branding-no' },
              ].map(opt => {
                const selected = canBrand === opt.value;
                const locked = savingBrand || user.role === 'admin';
                return (
                  <button
                    key={opt.label}
                    type="button"
                    role="radio"
                    aria-checked={selected}
                    data-testid={opt.testId}
                    disabled={locked}
                    onClick={() => handleToggleBranding(opt.value)}
                    style={{
                      minWidth: '78px',
                      padding: '8px 18px',
                      fontSize: '12px',
                      letterSpacing: '0.06em',
                      textTransform: 'uppercase',
                      fontWeight: selected ? '600' : '500',
                      background: selected ? 'var(--kahla-charcoal, #3a3a3a)' : 'transparent',
                      color: selected ? '#ffffff' : 'var(--kahla-text-light)',
                      border: 'none',
                      cursor: locked ? 'not-allowed' : 'pointer',
                      opacity: locked ? 0.55 : 1,
                      transition: 'background 0.18s ease, color 0.18s ease',
                    }}
                  >
                    {opt.label}
                  </button>
                );
              })}
            </div>

            <div style={{ fontSize: '11px', color: 'var(--kahla-text-light)', marginTop: '10px', fontStyle: 'italic' }}>
              {savingBrand
                ? 'Saving…'
                : (canBrand
                    ? 'Custom branding is currently allowed for this user.'
                    : 'This user sees the template defaults.')}
            </div>

            {user.role === 'admin' && (
              <div style={{ fontSize: '10px', color: 'var(--kahla-text-light)', marginTop: '6px', fontStyle: 'italic' }}>
                Admin always has branding access.
              </div>
            )}
          </div>
        </div>

        {/* Trial section */}
        <div>
          <div style={sectionLabel}>Trial</div>
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
            <button data-testid="modal-set-trial-3" disabled={busy} onClick={() => handleAction('set_trial', 3)} style={actionBtnStyle('#1565c0')}>Set 3-day trial</button>
            <span style={{ fontSize: '11px', color: 'var(--kahla-text-light)' }}>or</span>
            <input data-testid="modal-trial-date-input" type="date" value={trialDate} onChange={e => setTrialDate(e.target.value)} style={{ ...inputStyle, width: 'auto', padding: '6px 10px', fontSize: '12px' }} />
            <button data-testid="modal-set-trial-date" disabled={busy || !trialDate} onClick={() => handleAction('set_trial_date', undefined, new Date(trialDate + 'T23:59:59Z').toISOString())} style={{ ...actionBtnStyle('#1565c0'), opacity: trialDate ? 1 : 0.4 }}>Set date</button>
          </div>
        </div>

        {/* Extend access section */}
        <div>
          <div style={sectionLabel}>Extend Access</div>
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
            <button data-testid="modal-extend-3" disabled={busy} onClick={() => handleAction('extend_access', 3)} style={actionBtnStyle('#2e7d32')}>+3 days</button>
            <button data-testid="modal-extend-30" disabled={busy} onClick={() => handleAction('extend_access', 30)} style={actionBtnStyle('#2e7d32')}>+30 days</button>
            <button data-testid="modal-extend-90" disabled={busy} onClick={() => handleAction('extend_access', 90)} style={actionBtnStyle('#2e7d32')}>+90 days</button>
          </div>
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center', marginTop: '10px', flexWrap: 'wrap' }}>
            <span style={{ fontSize: '11px', color: 'var(--kahla-text-light)' }}>Set custom end date:</span>
            <input data-testid="modal-access-date-input" type="date" value={accessDate} onChange={e => setAccessDate(e.target.value)} style={{ ...inputStyle, width: 'auto', padding: '6px 10px', fontSize: '12px' }} />
            <button data-testid="modal-set-access-date" disabled={busy || !accessDate} onClick={() => handleAction('set_access_date', undefined, new Date(accessDate + 'T23:59:59Z').toISOString())} style={{ ...actionBtnStyle('#2e7d32'), opacity: accessDate ? 1 : 0.4 }}>Set date</button>
          </div>
        </div>

        {/* Actions section */}
        <div style={{ borderTop: '1px solid var(--kahla-border)', paddingTop: '16px' }}>
          <div style={sectionLabel}>Actions</div>
          <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
            {(st === 'trial' || st === 'active') && (
              <button data-testid="modal-mark-expired" disabled={busy} onClick={() => handleAction('mark_expired')} style={actionBtnStyle('#e65100')}>Mark as expired</button>
            )}
            {st === 'expired_read_only' && (
              <button data-testid="modal-archive" disabled={busy} onClick={() => handleAction('archive')} style={actionBtnStyle('#4e342e')}>Archive workspace</button>
            )}
            {(st === 'expired_read_only' || st === 'archived_frozen' || st === 'deactivated') && (
              <button data-testid="modal-restore-30" disabled={busy} onClick={() => handleAction('restore_access', 30)} style={actionBtnStyle('#2e7d32')}>Restore access (30 days)</button>
            )}
            {st !== 'deactivated' && st !== 'archived_frozen' && (
              <button data-testid="modal-deactivate" disabled={busy} onClick={() => handleAction('deactivate')} style={actionBtnStyle('#9e9e9e')}>Deactivate account</button>
            )}
          </div>
        </div>

      </div>
    </Modal>
  );
};

const renewalLabel = { fontSize: '10px', color: 'var(--kahla-text-light)', letterSpacing: '0.03em', marginBottom: '2px' };
const renewalValue = { fontSize: '13px', color: 'var(--kahla-text)' };

const PaymentBadge = ({ status }) => {
  const m = { pending: { bg: '#fff8e1', color: '#f57f17' }, paid: { bg: '#e8f5e9', color: '#2e7d32' }, declined: { bg: '#ffebee', color: '#c62828' } };
  const s = m[status] || m.pending;
  return <span data-testid={`payment-badge-${status}`} style={{ fontSize: '10px', padding: '2px 8px', background: s.bg, color: s.color, textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: '500' }}>{status}</span>;
};

const InvoiceBadge = ({ status }) => {
  const s = status === 'sent' ? { bg: '#e8f5e9', color: '#2e7d32', label: 'Sent' } : { bg: '#fafafa', color: '#9e9e9e', label: 'Not sent' };
  return <span data-testid={`invoice-badge-${status}`} style={{ fontSize: '10px', padding: '2px 8px', background: s.bg, color: s.color, textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: '500' }}>{s.label}</span>;
};

const RenewalRequestCard = ({ req, onAction, showToast, onPaymentLink }) => {
  const [busy, setBusy] = useState(false);

  const handle = async (fn, label) => {
    setBusy(true);
    try { await fn(); showToast(label); onAction(); }
    catch (err) { showToast(err.response?.data?.detail || 'Failed'); }
    finally { setBusy(false); }
  };

  const payStatus = req.payment_status || 'pending';
  const invStatus = req.invoice_status || 'not_sent';

  return (
    <div data-testid={`renewal-req-${req.id}`} style={{
      background: 'var(--kahla-white)', border: '1px solid var(--kahla-border)', padding: '16px 20px',
    }}>
      {/* Row 1: Name, email, badges */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap', marginBottom: '10px' }}>
        <span style={{ fontSize: '14px', fontWeight: '600', color: 'var(--kahla-text)' }}>{req.username}</span>
        <span style={{ fontSize: '12px', color: 'var(--kahla-text-light)' }}>{req.email}</span>
        <div style={{ marginLeft: 'auto', display: 'flex', gap: '6px', alignItems: 'center' }}>
          <PaymentBadge status={payStatus} />
          <InvoiceBadge status={invStatus} />
        </div>
      </div>

      {/* Row 2: Structured data grid */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))', gap: '12px', marginBottom: '12px' }}>
        <div>
          <div style={renewalLabel}>Request date</div>
          <div style={renewalValue}>{fmtDate(req.created_at)}</div>
        </div>
        <div>
          <div style={renewalLabel}>Current access until</div>
          <div style={renewalValue}>{req.current_access_expires_at ? fmtDate(req.current_access_expires_at) : '—'}</div>
        </div>
        <div>
          <div style={renewalLabel}>Requested extension</div>
          <div style={{ ...renewalValue, fontWeight: '600' }}>{req.period_label}</div>
        </div>
        {req.payment_amount && (
          <div>
            <div style={renewalLabel}>Amount</div>
            <div style={renewalValue}>{req.payment_amount}</div>
          </div>
        )}
      </div>

      {/* Row 3: Message */}
      {req.message && (
        <div style={{ fontSize: '12px', color: 'var(--kahla-text-light)', fontStyle: 'italic', marginBottom: '12px', paddingLeft: '8px', borderLeft: '2px solid var(--kahla-border)' }}>
          {req.message}
        </div>
      )}

      {/* Row 4: Actions */}
      <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', borderTop: '1px solid var(--kahla-border)', paddingTop: '12px' }}>
        <button data-testid={`renewal-payment-${req.id}`} disabled={busy} onClick={onPaymentLink} style={actionBtnStyle('#1565c0')}>Send payment link</button>
        {payStatus !== 'paid' && (
          <button data-testid={`renewal-mark-paid-${req.id}`} disabled={busy} onClick={() => handle(() => adminAPI.markRenewalPaid(req.id), 'Marked as paid')} style={actionBtnStyle('#2e7d32')}>Mark as paid</button>
        )}
        <button data-testid={`renewal-approve-${req.id}`} disabled={busy} onClick={() => handle(() => adminAPI.approveRenewal(req.id), 'Renewal approved')} style={actionBtnStyle('#2e7d32')}>Approve</button>
        <button data-testid={`renewal-decline-${req.id}`} disabled={busy} onClick={() => handle(() => adminAPI.declineRenewal(req.id), 'Request declined')} style={actionBtnStyle('#c62828')}>Decline</button>
      </div>
    </div>
  );
};

const RenewalHistoryCard = ({ req, onAction, showToast }) => {
  const [busy, setBusy] = useState(false);
  const sc = req.status === 'approved' ? { bg: '#e8f5e9', color: '#2e7d32' } : { bg: '#ffebee', color: '#c62828' };
  const payStatus = req.payment_status || (req.status === 'approved' ? 'paid' : 'pending');
  const invStatus = req.invoice_status || 'not_sent';

  const handleInvoice = async () => {
    setBusy(true);
    try { await adminAPI.sendInvoice(req.id); showToast('Invoice marked as sent'); onAction(); }
    catch (err) { showToast(err.response?.data?.detail || 'Failed'); }
    finally { setBusy(false); }
  };

  return (
    <div data-testid={`renewal-hist-${req.id}`} style={{
      background: 'var(--kahla-white)', border: '1px solid var(--kahla-border)', padding: '14px 20px',
    }}>
      {/* Row 1: Name, email, badges */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap', marginBottom: '8px' }}>
        <span style={{ fontSize: '13px', fontWeight: '600', color: 'var(--kahla-text)' }}>{req.username}</span>
        <span style={{ fontSize: '11px', color: 'var(--kahla-text-light)' }}>{req.email}</span>
        <div style={{ marginLeft: 'auto', display: 'flex', gap: '6px', alignItems: 'center' }}>
          <span style={{ fontSize: '10px', padding: '2px 8px', background: sc.bg, color: sc.color, textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: '500' }}>{req.status}</span>
          <PaymentBadge status={payStatus} />
          <InvoiceBadge status={invStatus} />
        </div>
      </div>

      {/* Row 2: Structured data */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(130px, 1fr))', gap: '10px' }}>
        <div>
          <div style={renewalLabel}>Request date</div>
          <div style={renewalValue}>{fmtDate(req.created_at)}</div>
        </div>
        <div>
          <div style={renewalLabel}>Old access until</div>
          <div style={renewalValue}>{req.old_access_end ? fmtDate(req.old_access_end) : '—'}</div>
        </div>
        <div>
          <div style={renewalLabel}>Extension</div>
          <div style={{ ...renewalValue, fontWeight: '600' }}>{req.period_label}</div>
        </div>
        <div>
          <div style={renewalLabel}>New access until</div>
          <div style={{ ...renewalValue, fontWeight: '600' }}>{req.new_access_end ? fmtDate(req.new_access_end) : '—'}</div>
        </div>
        {req.payment_amount && (
          <div>
            <div style={renewalLabel}>Amount</div>
            <div style={renewalValue}>{req.payment_amount}</div>
          </div>
        )}
        {req.admin_note && (
          <div>
            <div style={renewalLabel}>Note</div>
            <div style={{ ...renewalValue, fontSize: '12px' }}>{req.admin_note}</div>
          </div>
        )}
      </div>

      {/* Actions row */}
      <div style={{ marginTop: '10px', borderTop: '1px solid var(--kahla-border)', paddingTop: '8px', display: 'flex', gap: '8px', alignItems: 'center' }}>
        {req.status === 'approved' && invStatus !== 'sent' && (
          <button data-testid={`renewal-invoice-${req.id}`} disabled={busy} onClick={handleInvoice} style={actionBtnStyle('#5d4037')}>
            {busy ? '...' : 'Send invoice'}
          </button>
        )}
        {invStatus === 'sent' && req.invoice_sent_at && (
          <span style={{ fontSize: '10px', color: 'var(--kahla-text-light)' }}>
            Invoice sent: {fmtDate(req.invoice_sent_at)}
          </span>
        )}
        <button
          data-testid={`renewal-delete-${req.id}`}
          disabled={busy}
          onClick={async () => {
            if (!window.confirm('Permanently delete this renewal record?')) return;
            setBusy(true);
            try { await adminAPI.deleteRenewalRequest(req.id); showToast('Deleted'); onAction(); }
            catch (err) { showToast(err.response?.data?.detail || 'Failed'); }
            finally { setBusy(false); }
          }}
          style={{ ...actionBtnStyle('#c62828'), fontSize: '10px', padding: '3px 8px', marginLeft: 'auto' }}
        >Delete</button>
      </div>
    </div>
  );
};

const PaymentLinkModal = ({ req, onClose, onSent, showToast }) => {
  const [link, setLink] = useState('');
  const [amount, setAmount] = useState('');
  const [note, setNote] = useState('');
  const [sending, setSending] = useState(false);

  const handleSend = async () => {
    if (!link.trim()) return;
    setSending(true);
    try {
      await adminAPI.sendPaymentLink(req.id, { payment_link: link, amount, note });
      showToast('Payment link sent');
      onSent();
    } catch (err) {
      showToast(err.response?.data?.detail || 'Failed');
    } finally { setSending(false); }
  };

  return (
    <Modal onClose={onClose} title={`Send payment link — ${req.username}`}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
        <div style={{ fontSize: '12px', color: 'var(--kahla-text-light)', marginBottom: '4px' }}>
          Requesting: <strong style={{ color: 'var(--kahla-text)' }}>{req.period_label}</strong> · {req.email}
        </div>
        <FieldGroup label="Payment URL *">
          <input data-testid="payment-link-input" type="url" value={link} onChange={e => setLink(e.target.value)} placeholder="https://..." style={inputStyle} />
        </FieldGroup>
        <FieldGroup label="Amount (optional)">
          <input data-testid="payment-amount-input" type="text" value={amount} onChange={e => setAmount(e.target.value)} placeholder="e.g. 29 EUR" style={inputStyle} />
        </FieldGroup>
        <FieldGroup label="Note (optional)">
          <input data-testid="payment-note-input" type="text" value={note} onChange={e => setNote(e.target.value)} placeholder="Invoice reference, etc." style={inputStyle} />
        </FieldGroup>
        <button className="btn-primary" data-testid="send-payment-btn" onClick={handleSend} disabled={!link.trim() || sending} style={{ opacity: link.trim() ? 1 : 0.5 }}>
          {sending ? 'Sending...' : 'Send payment link'}
        </button>
      </div>
    </Modal>
  );
};

const BrandingPill = ({ user, onAction, showToast }) => {
  // Local optimistic state so the pill flips the instant the admin clicks it,
  // even before the parent refetches the users list.
  const [value, setValue] = useState(!!user.can_customize_branding);
  const [busy, setBusy] = useState(false);

  // Keep local state in sync when parent re-renders with fresh data
  React.useEffect(() => {
    setValue(!!user.can_customize_branding);
  }, [user.can_customize_branding]);

  const handleClick = async () => {
    if (busy) return;
    const next = !value;
    setValue(next);
    setBusy(true);
    try {
      await adminAPI.updateBrandingPermission(user.id, next);
      showToast(next ? 'Branding enabled' : 'Branding disabled — saved overrides preserved');
      onAction();
    } catch (err) {
      setValue(!next);
      showToast(err.response?.data?.detail || 'Failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <button
      type="button"
      data-testid={`branding-pill-${user.username}`}
      onClick={handleClick}
      disabled={busy}
      title={value ? 'Click to disable branding (data will be preserved)' : 'Click to enable custom branding'}
      style={{
        fontSize: '10px',
        padding: '2px 10px',
        background: value ? '#e8f5e9' : '#f5f5f5',
        color: value ? '#2e7d32' : '#9e9e9e',
        textTransform: 'uppercase',
        letterSpacing: '0.05em',
        fontWeight: '500',
        border: 'none',
        cursor: busy ? 'wait' : 'pointer',
        fontFamily: 'inherit',
        opacity: busy ? 0.6 : 1,
        transition: 'all 0.15s ease',
      }}
    >
      {value ? 'On' : 'Off'}
    </button>
  );
};

const SupportAccessCell = ({ user, showToast }) => {
  const [busy, setBusy] = useState(false);

  if (user.role === 'admin') {
    return <span style={{ fontSize: '11px', color: 'var(--kahla-text-light)', fontStyle: 'italic' }}>—</span>;
  }

  const st = user.support_access_status;
  const hours = user.support_access_hours_left;

  const handleOpenWorkspace = async () => {
    setBusy(true);
    try {
      const { data } = await adminAPI.impersonateUser(user.id);
      // Open in new tab with the impersonation token
      const url = new URL('/dashboard', window.location.origin);
      url.searchParams.set('support_token', data.access_token);
      window.open(url.toString(), '_blank');
    } catch (err) {
      showToast(err.response?.data?.detail || 'Cannot access workspace');
    } finally {
      setBusy(false);
    }
  };

  if (st === 'active') {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', alignItems: 'flex-start' }}>
        <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
          <span data-testid={`support-status-${user.username}`} style={{
            fontSize: '10px', padding: '2px 8px', background: '#e8f5e9', color: '#2e7d32',
            textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: '500',
          }}>Active</span>
          <span style={{ fontSize: '10px', color: 'var(--kahla-text-light)' }}>
            {hours != null ? `${hours < 1 ? '<1' : Math.floor(hours)}h left` : ''}
          </span>
        </div>
        <button
          data-testid={`open-workspace-${user.username}`}
          disabled={busy}
          onClick={handleOpenWorkspace}
          style={{
            padding: '3px 10px', fontSize: '10px', letterSpacing: '0.04em',
            border: '1px solid #1565c020', background: '#1565c008', color: '#1565c0',
            cursor: 'pointer', fontFamily: 'inherit', borderRadius: '2px',
          }}
        >{busy ? '...' : 'Open Workspace'}</button>
      </div>
    );
  }

  if (st === 'expired') {
    return <span data-testid={`support-status-${user.username}`} style={{ fontSize: '10px', color: 'var(--kahla-text-light)' }}>Expired</span>;
  }

  return <span data-testid={`support-status-${user.username}`} style={{ fontSize: '10px', color: 'var(--kahla-text-light)' }}>Disabled</span>;
};

const sectionHeading = {
  fontSize: '12px',
  textTransform: 'uppercase',
  letterSpacing: '0.1em',
  color: 'var(--kahla-text-light)',
  marginBottom: '14px',
  fontWeight: '500',
};

// Shared scene styles for the Footer-Everywhere preview card. Each scene is
// a tiny faux-rendering of one Maestra surface, live-bound to the ptx state.
const footerScene = {
  marginBottom: 14,
  border: '1px solid var(--kahla-border)',
  background: '#ffffff',
};
const footerSceneEyebrow = {
  fontSize: 9,
  letterSpacing: '0.22em',
  textTransform: 'uppercase',
  color: 'var(--kahla-text-light)',
  padding: '6px 10px',
  background: '#f3eee5',
  borderBottom: '1px solid var(--kahla-border)',
};
const footerSceneBody = {
  padding: '20px 16px 16px',
  textAlign: 'center',
};
const footerSceneFooterBand = {
  borderTop: '1px dashed var(--kahla-border)',
  paddingTop: 10,
};
const footerSceneText = {
  fontFamily: 'Playfair Display, serif',
  fontSize: 10,
  letterSpacing: '0.15em',
  color: 'var(--kahla-text-light)',
  marginBottom: 3,
};
const footerSceneSubtext = {
  fontSize: 9,
  letterSpacing: '0.08em',
  color: 'var(--kahla-text-light)',
  opacity: 0.7,
};
const footerSceneLink = {
  fontSize: 9,
  color: 'var(--kahla-text-light)',
  marginTop: 4,
  textDecoration: 'underline',
  opacity: 0.8,
};

const PreviewCard = ({ label, children }) => (
  <div data-testid={`preview-${label.toLowerCase().replace(/\s+/g, '-')}`} style={{
    border: '1px solid var(--kahla-border)',
    background: 'var(--kahla-cream, #faf8f5)',
    overflow: 'hidden',
  }}>
    <div style={{
      padding: '8px 12px',
      fontSize: '10px',
      textTransform: 'uppercase',
      letterSpacing: '0.08em',
      color: 'var(--kahla-text-light)',
      borderBottom: '1px solid var(--kahla-border)',
      background: 'var(--kahla-white)',
    }}>
      {label}
    </div>
    {children}
  </div>
);

const FieldGroup = ({ label, children }) => (
  <div>
    <label style={{ display: 'block', fontSize: '12px', color: 'var(--kahla-text-light)', marginBottom: '6px', letterSpacing: '0.03em' }}>
      {label}
    </label>
    {children}
  </div>
);

const ToggleField = ({ label, checked, onChange, testId }) => (
  <label
    data-testid={testId}
    style={{
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      fontSize: '12px',
      color: 'var(--kahla-text)',
      cursor: 'pointer',
      padding: '8px',
      border: '1px solid var(--kahla-border)',
      background: checked ? '#f8f6f3' : 'transparent',
    }}
  >
    <input
      type="checkbox"
      checked={checked}
      onChange={e => onChange(e.target.checked)}
      style={{ accentColor: 'var(--kahla-text)' }}
    />
    {label}
  </label>
);

const Modal = ({ onClose, title, children }) => (
  <div
    style={{
      position: 'fixed',
      inset: 0,
      background: 'rgba(0,0,0,0.3)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000,
    }}
    onClick={onClose}
  >
    <div
      style={{
        background: 'var(--kahla-cream, #faf8f5)',
        padding: '32px',
        maxWidth: '460px',
        width: '90%',
        maxHeight: '80vh',
        overflowY: 'auto',
        border: '1px solid var(--kahla-border)',
      }}
      onClick={e => e.stopPropagation()}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
        <h2 style={{ fontSize: '16px', fontWeight: '600', margin: 0, fontFamily: 'Playfair Display, serif' }}>{title}</h2>
        <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: '18px', cursor: 'pointer', color: 'var(--kahla-text-light)' }}>
          x
        </button>
      </div>
      {children}
    </div>
  </div>
);


// ==================== FEATURE LABEL REGISTRY ====================
//
// Friendly labels + 1-line editorial descriptions for every feature
// key the backend exposes. Shared by PlansTab and UserDetailsDrawer so
// both surfaces stay perfectly in sync. New keys added to FEATURE_KEYS
// on the backend will render with a fallback humanized label until an
// editorial copy entry is added here.
const FEATURE_LABELS = {
  allow_documents: { label: 'Documents', hint: 'Attach PDFs and reference files to a board.' },
  allow_notes: { label: 'Notes', hint: 'Internal and client-visible notes within a board.' },
  allow_client_uploads: { label: 'Client Uploads', hint: 'Client can add their own reference images.' },
  allow_branding: { label: 'Branding', hint: 'Custom logo, primary colour and footer attribution.' },
  allow_custom_texts: { label: 'Client View Texts', hint: 'Replace platform copy with the studio\u2019s own wording.' },
  allow_image_conversations: { label: 'Image Conversations', hint: 'Per-image client comments with owner replies.' },
  allow_shared_decision: { label: 'Shared Decision', hint: 'Multiple clients can agree on selections together.' },
  allow_final_decision_mode: { label: 'Final Decision Mode', hint: 'Email-verified final approval workflow.' },
  allow_client_notifications: { label: 'Client Notifications', hint: 'Notify-email button on each board.' },
  allow_custom_message: { label: 'Custom Client Message', hint: 'Personal message above the gallery.' },
  allow_custom_welcome: { label: 'Custom Welcome', hint: 'Per-board welcome title and description.' },
  allow_board_cover: { label: 'Board Cover Overlay', hint: 'Cinematic full-screen entrance before the board.' },
};

const humanizeFeatureKey = (key) => {
  if (FEATURE_LABELS[key]) return FEATURE_LABELS[key].label;
  return key.replace(/^allow_/, '').replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
};

const featureHint = (key) => FEATURE_LABELS[key]?.hint || '';


// ==================== PLANS TAB ====================
//
// Editorial table view — rows are features, columns are plans. The
// table doubles as both a read-only overview and the in-place editor:
// click any cell to toggle the feature for that plan, drag a plan into
// place via the order field, rename/describe inline. New plans are
// created via the calm "Add plan" button at the bottom of the column
// header row.
const PlansTab = ({ plans, featureKeys, onChange, showToast }) => {
  const [creating, setCreating] = useState(false);
  const [newName, setNewName] = useState('');
  const [newDescription, setNewDescription] = useState('');

  const sorted = [...(plans || [])].sort((a, b) => (a.order ?? 99) - (b.order ?? 99));

  const togglePlanFeature = async (plan, key, next) => {
    const features = { ...(plan.features || {}), [key]: next };
    Object.keys(features).forEach(k => { if (!features[k]) delete features[k]; });
    try {
      await adminAPI.updatePlan(plan.id, {
        name: plan.name, description: plan.description || '', features, order: plan.order,
      });
      onChange();
    } catch (err) {
      showToast(err.response?.data?.detail || 'Failed to update plan');
    }
  };

  const renamePlan = async (plan, nextName, nextDescription) => {
    try {
      await adminAPI.updatePlan(plan.id, {
        name: nextName, description: nextDescription, features: plan.features || {}, order: plan.order,
      });
      onChange();
    } catch (err) {
      showToast(err.response?.data?.detail || 'Failed to update plan');
    }
  };

  const handleCreate = async () => {
    if (!newName.trim()) return;
    try {
      await adminAPI.createPlan({ name: newName.trim(), description: newDescription.trim(), features: {} });
      setCreating(false);
      setNewName('');
      setNewDescription('');
      onChange();
      showToast('Plan created');
    } catch (err) {
      showToast(err.response?.data?.detail || 'Failed to create plan');
    }
  };

  const handleDelete = async (plan) => {
    if (!window.confirm(`Delete the "${plan.name}" plan? Users assigned to it must be reassigned first.`)) return;
    try {
      await adminAPI.deletePlan(plan.id);
      onChange();
      showToast('Plan deleted');
    } catch (err) {
      showToast(err.response?.data?.detail || 'Cannot delete plan');
    }
  };

  return (
    <div data-testid="plans-tab" style={{ maxWidth: 1240 }}>
      <p style={{
        fontFamily: 'Cormorant Garamond, Playfair Display, serif',
        fontStyle: 'italic', fontSize: 16, lineHeight: 1.65,
        color: 'var(--kahla-text-light)', margin: '0 0 32px',
        maxWidth: 640,
      }}>
        Plans are the studio&rsquo;s feature packages. Templates handle workflow defaults &mdash; labels, language, section order. Plans decide which premium capabilities each workspace unlocks.
      </p>

      {sorted.length === 0 ? (
        <div style={{ padding: '36px 0', color: 'var(--kahla-text-light)', fontSize: 13, fontStyle: 'italic' }}>
          No plans yet. The defaults will appear on next backend boot.
        </div>
      ) : (
        <div style={{ overflowX: 'auto', border: '1px solid var(--kahla-border)', background: 'var(--kahla-white)' }}>
          <table data-testid="plans-table" style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr>
                <th style={{ ...thStyle, width: '34%', borderBottom: '1px solid var(--kahla-border)', padding: '16px 18px' }}>Feature</th>
                {sorted.map(p => (
                  <PlanHeaderCell key={p.id} plan={p} onRename={renamePlan} onDelete={() => handleDelete(p)} />
                ))}
              </tr>
            </thead>
            <tbody>
              {(featureKeys || []).map(key => (
                <tr key={key} data-testid={`plan-feature-row-${key}`} style={{ borderTop: '1px solid var(--kahla-border)' }}>
                  <td style={{ padding: '14px 18px', verticalAlign: 'top' }}>
                    <div style={{ fontSize: 13, fontWeight: 500, color: 'var(--kahla-text)' }}>{humanizeFeatureKey(key)}</div>
                    <div style={{ fontSize: 11, color: 'var(--kahla-text-light)', marginTop: 3, fontStyle: 'italic' }}>{featureHint(key)}</div>
                  </td>
                  {sorted.map(p => (
                    <td key={p.id} style={{ padding: '14px 12px', verticalAlign: 'top', textAlign: 'center' }}>
                      <PlanCellToggle
                        plan={p}
                        featureKey={key}
                        value={!!(p.features || {})[key]}
                        onToggle={(next) => togglePlanFeature(p, key, next)}
                      />
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div style={{ marginTop: 28 }}>
        {!creating ? (
          <button
            data-testid="create-plan-btn"
            onClick={() => setCreating(true)}
            style={{
              background: 'none', border: '1px solid var(--kahla-border)',
              padding: '10px 22px', fontSize: 11, letterSpacing: '0.18em',
              textTransform: 'uppercase', color: 'var(--kahla-text)',
              cursor: 'pointer', fontFamily: 'inherit',
            }}
          >Add plan</button>
        ) : (
          <div style={{ border: '1px solid var(--kahla-border)', background: '#faf8f5', padding: '20px 22px', maxWidth: 560 }}>
            <div style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.12em', color: 'var(--kahla-text-light)', marginBottom: 14 }}>New plan</div>
            <input
              data-testid="new-plan-name"
              type="text"
              placeholder="Plan name (e.g. Studio)"
              value={newName}
              onChange={e => setNewName(e.target.value)}
              style={{ ...inputStyle, marginBottom: 10 }}
            />
            <input
              data-testid="new-plan-description"
              type="text"
              placeholder="One-line description (optional)"
              value={newDescription}
              onChange={e => setNewDescription(e.target.value)}
              style={inputStyle}
            />
            <div style={{ display: 'flex', gap: 10, marginTop: 14, justifyContent: 'flex-end' }}>
              <button onClick={() => { setCreating(false); setNewName(''); setNewDescription(''); }} style={{ background: 'none', border: 'none', padding: '8px 14px', fontSize: 12, color: 'var(--kahla-text-light)', cursor: 'pointer' }}>Cancel</button>
              <button data-testid="confirm-create-plan" disabled={!newName.trim()} onClick={handleCreate} className="btn-primary">Create plan</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const PlanHeaderCell = ({ plan, onRename, onDelete }) => {
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(plan.name);
  const [desc, setDesc] = useState(plan.description || '');

  useEffect(() => { setName(plan.name); setDesc(plan.description || ''); }, [plan.id, plan.name, plan.description]);

  if (!editing) {
    return (
      <th style={{ ...thStyle, padding: '16px 18px', textAlign: 'left', borderBottom: '1px solid var(--kahla-border)', minWidth: 180 }}>
        <div style={{ fontFamily: 'Playfair Display, serif', fontSize: 17, fontWeight: 400, color: 'var(--kahla-text)', textTransform: 'none', letterSpacing: '-0.005em', marginBottom: 4 }}>
          {plan.name}
        </div>
        {plan.description && (
          <div style={{ fontSize: 11, color: 'var(--kahla-text-light)', fontStyle: 'italic', textTransform: 'none', letterSpacing: 0, lineHeight: 1.45, fontWeight: 400, maxWidth: 220 }}>
            {plan.description}
          </div>
        )}
        <div style={{ display: 'flex', gap: 10, marginTop: 8 }}>
          <button data-testid={`plan-edit-${plan.id}`} onClick={() => setEditing(true)} style={{ background: 'none', border: 'none', padding: 0, fontSize: 10, letterSpacing: '0.1em', textTransform: 'uppercase', color: 'var(--kahla-text-light)', cursor: 'pointer', fontFamily: 'inherit' }}>Rename</button>
          <button data-testid={`plan-delete-${plan.id}`} onClick={onDelete} style={{ background: 'none', border: 'none', padding: 0, fontSize: 10, letterSpacing: '0.1em', textTransform: 'uppercase', color: '#c62828', cursor: 'pointer', fontFamily: 'inherit' }}>Delete</button>
        </div>
      </th>
    );
  }

  return (
    <th style={{ padding: '14px 16px', borderBottom: '1px solid var(--kahla-border)', minWidth: 200 }}>
      <input
        data-testid={`plan-name-input-${plan.id}`}
        value={name}
        onChange={e => setName(e.target.value)}
        style={{ ...inputStyle, fontSize: 13, marginBottom: 8 }}
      />
      <input
        data-testid={`plan-desc-input-${plan.id}`}
        value={desc}
        onChange={e => setDesc(e.target.value)}
        placeholder="Description"
        style={{ ...inputStyle, fontSize: 12 }}
      />
      <div style={{ display: 'flex', gap: 8, marginTop: 8, justifyContent: 'flex-end' }}>
        <button onClick={() => { setEditing(false); setName(plan.name); setDesc(plan.description || ''); }} style={{ background: 'none', border: 'none', padding: '6px 10px', fontSize: 11, color: 'var(--kahla-text-light)', cursor: 'pointer' }}>Cancel</button>
        <button data-testid={`plan-save-${plan.id}`} onClick={() => { onRename(plan, name.trim(), desc.trim()); setEditing(false); }} className="btn-primary" style={{ fontSize: 11, padding: '6px 14px' }}>Save</button>
      </div>
    </th>
  );
};

const PlanCellToggle = ({ plan, featureKey, value, onToggle }) => {
  return (
    <button
      data-testid={`plan-cell-${plan.id}-${featureKey}`}
      aria-pressed={value}
      onClick={() => onToggle(!value)}
      style={{
        width: 28, height: 28, borderRadius: '50%',
        background: value ? '#cf9a3d' : 'transparent',
        border: value ? '1px solid #cf9a3d' : '1px solid var(--kahla-border)',
        cursor: 'pointer', transition: 'background 0.2s ease, border-color 0.2s ease',
        padding: 0,
      }}
      title={value ? 'Included' : 'Not included'}
    />
  );
};


// ==================== USER DETAILS DRAWER ====================
//
// Right-side drawer for per-user details. Phase 2 surfaces Template,
// Plan, and Feature Overrides. Calm typography, generous spacing, no
// modal chrome. Future iterations will fold Branding / Limits / Status
// into this same drawer (Phase 3 cleanup).
const UserDetailsDrawer = ({ user, templates, plans, featureKeys, onClose, onAction, showToast }) => {
  const [planId, setPlanId] = useState(user?.plan_id || '');
  const [templateId, setTemplateId] = useState(user?.template_id || '');
  const [overrides, setOverrides] = useState(user?.feature_overrides || {});
  const [savingPlan, setSavingPlan] = useState(false);
  const [savingTemplate, setSavingTemplate] = useState(false);
  const [note, setNote] = useState('');

  useEffect(() => {
    if (!user) return;
    setPlanId(user.plan_id || '');
    setTemplateId(user.template_id || '');
    setOverrides(user.feature_overrides || {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  useEffect(() => {
    if (!note) return;
    const id = setTimeout(() => setNote(''), 3000);
    return () => clearTimeout(id);
  }, [note]);

  if (!user) return null;

  const currentPlan = plans.find(p => p.id === planId);
  const planDefaults = (currentPlan?.features) || {};

  const resolveValue = (key) => {
    if (key in overrides) return !!overrides[key];
    return !!planDefaults[key];
  };

  const overrideState = (key) => {
    if (!(key in overrides)) return 'plan';
    return overrides[key] ? 'on' : 'off';
  };

  const cycleOverride = async (key) => {
    const state = overrideState(key);
    let next;
    let nextOverrides = { ...overrides };
    if (state === 'plan') {
      next = !planDefaults[key];
      nextOverrides[key] = next;
    } else if (state === 'on') {
      next = false;
      nextOverrides[key] = false;
    } else {
      next = null;
      delete nextOverrides[key];
    }
    const prev = overrides;
    setOverrides(nextOverrides);
    try {
      await adminAPI.setUserFeatureOverrides(user.id, { [key]: next });
      onAction();
      setNote(next === null ? 'Override cleared \u2014 using plan default.' : `Override set to ${next ? 'On' : 'Off'}.`);
    } catch (err) {
      setOverrides(prev);
      showToast(err.response?.data?.detail || 'Failed to update override');
    }
  };

  const handlePlanChange = async (newPlanId) => {
    if (newPlanId === planId) return;
    setSavingPlan(true);
    const prev = planId;
    setPlanId(newPlanId);
    try {
      await adminAPI.setUserPlan(user.id, newPlanId);
      onAction();
      setNote('Plan updated.');
    } catch (err) {
      setPlanId(prev);
      showToast(err.response?.data?.detail || 'Failed to update plan');
    } finally {
      setSavingPlan(false);
    }
  };

  const handleTemplateChange = async (newTplId) => {
    if (newTplId === templateId) return;
    setSavingTemplate(true);
    const prev = templateId;
    setTemplateId(newTplId);
    try {
      await adminAPI.reassignUserTemplate(user.id, newTplId);
      onAction();
      setNote('Template updated.');
    } catch (err) {
      setTemplateId(prev);
      showToast(err.response?.data?.detail || 'Failed to update template');
    } finally {
      setSavingTemplate(false);
    }
  };

  const sectionLabel = { fontSize: 10, letterSpacing: '0.18em', textTransform: 'uppercase', color: 'var(--kahla-text-light)', marginBottom: 14, fontWeight: 500 };
  const selectStyle = { ...inputStyle, fontSize: 13 };

  return (
    <div
      data-testid="user-details-drawer"
      onClick={onClose}
      style={{
        position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
        background: 'rgba(20, 16, 10, 0.32)', zIndex: 200,
        display: 'flex', justifyContent: 'flex-end',
        backdropFilter: 'blur(2px)',
      }}
    >
      <aside
        onClick={e => e.stopPropagation()}
        style={{
          width: 'min(540px, 92vw)', height: '100vh',
          background: 'var(--kahla-white)', overflowY: 'auto',
          boxShadow: '-2px 0 24px rgba(0,0,0,0.06)',
          padding: '36px 40px 56px',
          boxSizing: 'border-box',
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 28 }}>
          <div>
            <div style={{ fontSize: 10, letterSpacing: '0.18em', textTransform: 'uppercase', color: 'var(--kahla-text-light)', marginBottom: 6 }}>User details</div>
            <h2 style={{ fontFamily: 'Playfair Display, serif', fontSize: 26, fontWeight: 400, margin: 0, letterSpacing: '-0.01em', color: 'var(--kahla-charcoal, #2a2a2a)' }}>{user.username}</h2>
            {user.email && <div style={{ fontSize: 12, color: 'var(--kahla-text-light)', marginTop: 4 }}>{user.email}</div>}
          </div>
          <button data-testid="drawer-close" onClick={onClose} style={{ background: 'none', border: 'none', fontSize: 20, cursor: 'pointer', color: 'var(--kahla-text-light)', padding: 4, lineHeight: 1 }}>&times;</button>
        </div>

        {note && (
          <div data-testid="drawer-note" style={{ padding: '10px 14px', background: '#faf6ec', border: '1px solid #efe2bf', fontSize: 12, color: '#6e5320', marginBottom: 28, fontStyle: 'italic' }}>
            {note}
          </div>
        )}

        <section style={{ marginBottom: 36 }}>
          <div style={sectionLabel}>Template</div>
          <select
            data-testid="drawer-template-select"
            value={templateId}
            disabled={savingTemplate}
            onChange={e => handleTemplateChange(e.target.value)}
            style={selectStyle}
          >
            <option value="">No template</option>
            {(templates || []).map(t => (
              <option key={t.id} value={t.id}>{t.name}{t.is_global ? ' (global)' : ''}</option>
            ))}
          </select>
          <div style={{ fontSize: 11, color: 'var(--kahla-text-light)', marginTop: 8, fontStyle: 'italic', lineHeight: 1.5 }}>
            Templates shape the workflow defaults &mdash; labels, language, section order.
          </div>
        </section>

        <section style={{ marginBottom: 36 }}>
          <div style={sectionLabel}>Plan</div>
          <select
            data-testid="drawer-plan-select"
            value={planId}
            disabled={savingPlan}
            onChange={e => handlePlanChange(e.target.value)}
            style={selectStyle}
          >
            <option value="">No plan (free tier)</option>
            {[...(plans || [])].sort((a, b) => (a.order ?? 99) - (b.order ?? 99)).map(p => (
              <option key={p.id} value={p.id}>{p.name}</option>
            ))}
          </select>
          {currentPlan?.description && (
            <div style={{ fontSize: 11, color: 'var(--kahla-text-light)', marginTop: 8, fontStyle: 'italic', lineHeight: 1.5 }}>
              {currentPlan.description}
            </div>
          )}
        </section>

        <section style={{ marginBottom: 36 }}>
          <div style={sectionLabel}>Feature access</div>
          <div style={{ fontSize: 11, color: 'var(--kahla-text-light)', marginBottom: 18, fontStyle: 'italic', lineHeight: 1.5 }}>
            Each feature follows the plan by default. Click to override &mdash; the change applies only to this user.
          </div>
          <div style={{ display: 'flex', flexDirection: 'column' }}>
            {(featureKeys || []).map(key => {
              const state = overrideState(key);
              const effective = resolveValue(key);
              return (
                <div
                  key={key}
                  data-testid={`drawer-feature-row-${key}`}
                  style={{
                    display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between',
                    padding: '14px 0', borderBottom: '1px solid var(--kahla-border)',
                    gap: 18,
                  }}
                >
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 13, fontWeight: 500, color: 'var(--kahla-text)' }}>{humanizeFeatureKey(key)}</div>
                    <div style={{ fontSize: 11, color: 'var(--kahla-text-light)', marginTop: 3, fontStyle: 'italic', lineHeight: 1.45 }}>{featureHint(key)}</div>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexShrink: 0 }}>
                    {state !== 'plan' && (
                      <span style={{ fontSize: 9, letterSpacing: '0.12em', textTransform: 'uppercase', color: '#7a5b2e', padding: '2px 6px', border: '1px solid #d8cab0' }}>
                        Override
                      </span>
                    )}
                    <button
                      data-testid={`drawer-feature-toggle-${key}`}
                      onClick={() => cycleOverride(key)}
                      style={{
                        background: 'none', border: 'none', padding: '4px 0',
                        cursor: 'pointer', fontFamily: 'inherit',
                        fontSize: 11, letterSpacing: '0.1em', textTransform: 'uppercase',
                        color: effective ? '#2a2a2a' : 'var(--kahla-text-light)',
                        fontWeight: state === 'plan' ? 400 : 600,
                        minWidth: 72, textAlign: 'right',
                      }}
                      title={state === 'plan' ? 'Plan default \u2014 click to override' : 'Override \u2014 click again to cycle'}
                    >
                      {effective ? 'On' : 'Off'}
                      {state === 'plan' && (
                        <span style={{ fontSize: 9, marginLeft: 6, color: 'var(--kahla-text-light)', letterSpacing: '0.08em' }}>(plan)</span>
                      )}
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
          {Object.keys(overrides).length > 0 && (
            <button
              data-testid="drawer-clear-overrides"
              onClick={async () => {
                if (!window.confirm('Clear all per-user overrides? The user will revert to plan defaults.')) return;
                const payload = {};
                Object.keys(overrides).forEach(k => { payload[k] = null; });
                try {
                  await adminAPI.setUserFeatureOverrides(user.id, payload);
                  setOverrides({});
                  onAction();
                  setNote('All overrides cleared.');
                } catch (err) {
                  showToast(err.response?.data?.detail || 'Failed');
                }
              }}
              style={{ background: 'none', border: 'none', padding: 0, marginTop: 18, fontSize: 11, letterSpacing: '0.1em', textTransform: 'uppercase', color: 'var(--kahla-text-light)', cursor: 'pointer', fontFamily: 'inherit' }}
            >Clear all overrides</button>
          )}
        </section>

        <div style={{ borderTop: '1px solid var(--kahla-border)', paddingTop: 18, fontSize: 11, color: 'var(--kahla-text-light)', lineHeight: 1.7 }}>
          <div>Created &middot; {user.created_at ? new Date(user.created_at).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : '\u2014'}</div>
          {user.storage_used_mb != null && (
            <div>Storage &middot; {user.storage_used_mb} MB{user.storage_limit_mb ? ` of ${user.storage_limit_mb} MB` : ''}</div>
          )}
          <div>Boards &middot; {user.board_count ?? 0}{user.max_boards != null ? ` of ${user.max_boards}` : ''}</div>
        </div>
      </aside>
    </div>
  );
};


export default AdminPanel;
